<template>
  <div class="scvd-dashboard">
    <div class="scvd-container">

      <!-- ============ ① HEADER ============ -->
      <div class="scvd-header">
        <div>
          <h1>Stock Cover Days Dashboard</h1>
          <div class="subtitle">แดชบอร์ดจำนวนวันคงคลัง — สินค้าประเภทน้ำเบียร์สด</div>
        </div>
        <div class="meta">
          <div>อัปเดตล่าสุด: {{ dashboard ? dashboard.currentDate : '—' }}</div>
          <div class="sync-status" v-if="!(dashboard && dashboard.isClosed)"><span class="sync-dot"></span> ข้อมูล ณ ปัจจุบัน</div>
          <!-- P4a: Monthly Snapshot — selected month is CLOSED (frozen snapshot) -->
          <div v-else class="scvd-frozen-badge"
               style="display:inline-block; background:#e0e7ff; color:#3730a3; border:1px solid #c7d2fe; padding:2px 10px; border-radius:20px; font-size:11.5px; font-weight:700;">
            🔒 ปิดรอบแล้ว — ข้อมูลถูกแช่แข็ง (frozen)
          </div>
        </div>
      </div>

      <!-- ============ ① CRITERIA BAR ============ -->
      <div class="setup-collapsed">
        <div class="criteria-summary">
          <span style="font-weight:bold;">⚙️ เกณฑ์:</span>
          <template v-if="criteriaLevels.length">
            <span
              v-for="lv in criteriaLevels"
              :key="'crit-' + lv.order"
              class="criteria-badge"
              :style="{ background: lv.colorHex, color: textColor(lv.colorHex) }"
            >{{ badgeLabel(lv) }}</span>
          </template>
          <span v-else class="text-muted" style="font-size:12px;">กำลังโหลดเกณฑ์...</span>
          <span style="margin-left:12px; color:#cbd5e1;">|</span>
          <span class="target-badge">🎯 Target SCVD (จำลอง): {{ criteria ? criteria.targetScvd : '-' }} วัน</span>
        </div>
        <button class="btn-edit" @click="openSetup">⚙️ แก้ไขเกณฑ์</button>
      </div>

      <!-- ============ ① FILTER BAR ============ -->
      <div class="filter-bar">
        <div class="filter-group">
          <label>เดือน</label>
          <select class="form-control" v-model.number="filterMonth">
            <option v-for="m in monthOptions" :key="'mo-' + m.value" :value="m.value">{{ m.text }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label>ปี</label>
          <select class="form-control" v-model.number="filterYear">
            <option v-for="y in yearOptions" :key="'yr-' + y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div class="filter-group" style="min-width:190px;">
          <label>คลัง</label>
          <select2
            :css-style="'width: 100%;'"
            :css-class="'form-control m-select2'"
            :id="'dbPlantCodes'"
            :name="'dbPlantCodes'"
            :select2-options="plantOptions"
            v-model="filterPlantCodes"
            ref="dbPlantCodesSelect2"
            multiple
          ></select2>
        </div>
        <div class="filter-group" style="min-width:210px;">
          <label>ชื่อสินค้า</label>
          <select2
            :css-style="'width: 100%;'"
            :css-class="'form-control m-select2'"
            :id="'dbProductCodes'"
            :name="'dbProductCodes'"
            :select2-options="productOptions"
            v-model="filterProductCodes"
            ref="dbProductCodesSelect2"
            multiple
          ></select2>
        </div>
        <div class="filter-group" style="min-width:170px;">
          <label>แบรนด์สินค้า</label>
          <select2
            :css-style="'width: 100%;'"
            :css-class="'form-control m-select2'"
            :id="'dbBrandId'"
            :name="'dbBrandId'"
            :select2-options="brandOptions"
            v-model="filterBrandIds"
            ref="dbBrandIdSelect2"
            multiple
          ></select2>
        </div>
        <button class="btn-formula" @click="openFormula">📐 สูตรคำนวณ</button>
      </div>

      <!-- ============ ① ACTIVE FILTER STRIP ============ -->
      <div class="active-filter-strip">
        <span class="filter-strip-label">🎯 กำลังดูข้อมูล:</span>
        <span class="filter-chip">เดือน: <strong>{{ monthName }} {{ filterYear }}</strong></span>
        <span class="filter-chip">
          คลัง: <strong>{{ filterPlantCodes.length ? (filterPlantCodes.length + ' คลัง') : 'ทั้งหมด' }}</strong>
          <span v-if="filterPlantCodes.length" class="remove-chip" @click="removeFilter('plant')">×</span>
        </span>
        <span class="filter-chip">
          สินค้า: <strong>{{ productChipText }}</strong>
          <span v-if="filterProductCodes.length" class="remove-chip" @click="removeFilter('product')">×</span>
        </span>
        <span v-if="filterBrandIds.length" class="filter-chip">
          แบรนด์: <strong>{{ filterBrandIds.length }} แบรนด์</strong>
          <span class="remove-chip" @click="removeFilter('brand')">×</span>
        </span>
        <button class="btn-clear-filter" @click="clearFilters">↺ ล้างตัวกรอง</button>
      </div>

      <!-- ============ LOADING / EMPTY ============ -->
      <div v-if="isLoading" class="scvd-loading">
        <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
        <div class="mt-3">กำลังโหลดข้อมูล...</div>
      </div>

      <!-- ============ DASHBOARD GRID ============ -->
      <div v-else-if="dashboard" class="dashboard">

        <!-- ===== ROW 1: ② Urgent (3fr) + Pie (2fr) ===== -->
        <div class="row-2col">

          <!-- ② URGENT CARDS -->
          <div class="widget">
            <div class="widget-header">
              <div class="widget-title">🚨 สินค้าที่ต้องดำเนินการเร่งด่วน<span
                  class="info-tooltip"
                  data-tip='คอลัมน์ซ้าย/ขวา = สถานะลำดับที่ 1 และ 2 จากหน้ากำหนดเกณฑ์ (ถ้าสลับลำดับใน "แผงตั้งค่าเกณฑ์" การจัดคอลัมน์จะสลับตาม)'
                >i</span></div>
              <div class="widget-hint">💡 คลิกที่การ์ด → ตัวกรองทั้งหน้าจะเปลี่ยนเป็นสินค้านั้น</div>
            </div>

            <!-- Summary bar -->
            <div class="alert-summary">
              <div class="alert-summary-item"
                :style="{ background: tint(dashboard.urgent.tier1.colorHex, 0.9), borderLeft: '3px solid ' + dashboard.urgent.tier1.colorHex }">
                <div class="alert-summary-top">
                  <div>
                    <div class="alert-summary-count-row">
                      <span class="alert-summary-count">{{ dashboard.urgent.tier1Count }}</span>
                      <span class="alert-summary-unit">SKU</span>
                    </div>
                    <div class="alert-summary-desc">ต้องผลิตด่วนที่สุด</div>
                  </div>
                  <div class="alert-summary-label">
                    <span class="status-swatch" :style="{ background: dashboard.urgent.tier1.colorHex }"></span>{{ dashboard.urgent.tier1.name }}<br>({{ rangeForOrder(dashboard.urgent.tier1.order) }})
                  </div>
                </div>
                <div class="alert-summary-guidance" :style="{ color: darken(dashboard.urgent.tier1.colorHex, 0.5) }">
                  ⚠️ {{ dashboard.urgent.tier1.actionText }}
                </div>
              </div>
              <div class="alert-summary-item"
                :style="{ background: tint(dashboard.urgent.tier2.colorHex, 0.9), borderLeft: '3px solid ' + dashboard.urgent.tier2.colorHex }">
                <div class="alert-summary-top">
                  <div>
                    <div class="alert-summary-count-row">
                      <span class="alert-summary-count">{{ dashboard.urgent.tier2Count }}</span>
                      <span class="alert-summary-unit">SKU</span>
                    </div>
                    <div class="alert-summary-desc">ควรวางแผนผลิต</div>
                  </div>
                  <div class="alert-summary-label">
                    <span class="status-swatch" :style="{ background: dashboard.urgent.tier2.colorHex }"></span>{{ dashboard.urgent.tier2.name }}<br>({{ rangeForOrder(dashboard.urgent.tier2.order) }})
                  </div>
                </div>
                <div class="alert-summary-guidance" :style="{ color: darken(dashboard.urgent.tier2.colorHex, 0.5) }">
                  📦 {{ dashboard.urgent.tier2.actionText }}
                </div>
              </div>
            </div>

            <!-- 2 tier columns -->
            <div class="alert-grid">
              <div class="alert-column">
                <template v-if="dashboard.urgent.tier1.cards.length">
                  <div
                    v-for="(card, ci) in dashboard.urgent.tier1.cards"
                    :key="'t1-' + ci"
                    class="alert-card"
                    :style="{ borderLeftColor: card.colorHex }"
                    @click="cardClick(card)"
                  >
                    <div class="alert-card-top">
                      <span class="alert-top-left">
                        <span class="alert-status" :style="{ background: tint(card.colorHex, 0.8), color: darken(card.colorHex, 0.35) }"><span class="status-swatch" :style="{ background: card.colorHex }"></span>{{ dashboard.urgent.tier1.name }}</span>
                      </span>
                      <span class="alert-trend" :class="trendInfo(card).cls">{{ trendInfo(card).text }}</span>
                    </div>
                    <div class="alert-sku">🍺 {{ card.productName }}</div>
                    <div class="alert-warehouse">🏭 {{ card.plantName }}</div>
                    <div class="alert-metric-row">
                      <div class="alert-metric-left">
                        <span class="alert-value" :style="{ color: darken(card.colorHex, 0.35) }">{{ scvdDisplay(card.scvd) }}</span>
                        <span class="alert-unit">วัน</span>
                        <span class="alert-metric-label">SCVD ปัจจุบัน</span>
                      </div>
                      <div class="alert-target">
                        <span class="alert-target-src">— จากข้อมูลตามหน้างานจริง —</span><br>
                        Demand {{ fmt(card.demandPerDayLitre) }} L ({{ fmt(card.demandPerDayKeg) }} ถัง)/วัน<br>
                        Supply {{ fmt(card.supplyPerDayLitre) }} L ({{ fmt(card.supplyPerDayKeg) }} ถัง)/วัน
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="alert-empty-state">
                  <span class="empty-emoji">✅</span>
                  <span>ไม่มีสินค้าในสถานะนี้</span>
                </div>
              </div>

              <div class="alert-column">
                <template v-if="dashboard.urgent.tier2.cards.length">
                  <div
                    v-for="(card, ci) in dashboard.urgent.tier2.cards"
                    :key="'t2-' + ci"
                    class="alert-card"
                    :style="{ borderLeftColor: card.colorHex }"
                    @click="cardClick(card)"
                  >
                    <div class="alert-card-top">
                      <span class="alert-status" :style="{ background: tint(card.colorHex, 0.8), color: darken(card.colorHex, 0.35) }"><span class="status-swatch" :style="{ background: card.colorHex }"></span>{{ dashboard.urgent.tier2.name }}</span>
                      <span class="alert-trend" :class="trendInfo(card).cls">{{ trendInfo(card).text }}</span>
                    </div>
                    <div class="alert-sku">🍺 {{ card.productName }}</div>
                    <div class="alert-warehouse">🏭 {{ card.plantName }}</div>
                    <div class="alert-metric-row">
                      <div class="alert-metric-left">
                        <span class="alert-value" :style="{ color: darken(card.colorHex, 0.35) }">{{ scvdDisplay(card.scvd) }}</span>
                        <span class="alert-unit">วัน</span>
                        <span class="alert-metric-label">SCVD ปัจจุบัน</span>
                      </div>
                      <div class="alert-target">
                        <span class="alert-target-src">— จากข้อมูลตามหน้างานจริง —</span><br>
                        Demand {{ fmt(card.demandPerDayLitre) }} L ({{ fmt(card.demandPerDayKeg) }} ถัง)/วัน<br>
                        Supply {{ fmt(card.supplyPerDayLitre) }} L ({{ fmt(card.supplyPerDayKeg) }} ถัง)/วัน
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="alert-empty-state">
                  <span class="empty-emoji">✅</span>
                  <span>ไม่มีสินค้าในสถานะนี้</span>
                </div>
              </div>
            </div>
          </div>

          <!-- ② PIE (D1: 3-level in-pie drill-down — สถานะ → คลัง → สินค้า) -->
          <div class="widget">
            <div class="widget-header">
              <div class="widget-title">🥧 สัดส่วนสถานะสต็อก {{ dashboard.pie.slices.length }} ระดับ</div>
              <div class="widget-hint">💡 คลิกวง → เจาะลึก สถานะ → คลัง → สินค้า</div>
            </div>

            <!-- drill breadcrumb -->
            <div class="pie-drill-bar">
              <span class="pie-crumbs">
                <template v-for="(c, ci) in pieBreadcrumb">
                  <span
                    :key="'crumb-' + ci"
                    class="pie-crumb"
                    :class="{ active: ci === pieBreadcrumb.length - 1 }"
                    @click="drillToLevel(c.level)"
                  >{{ c.label }}</span><span v-if="ci < pieBreadcrumb.length - 1" :key="'sep-' + ci" class="pie-crumb-sep">›</span>
                </template>
              </span>
              <button v-if="pieDrill.level > 1" class="pie-back-btn" @click="drillUp">↩ ย้อนกลับ</button>
            </div>

            <div class="pie-container" v-if="pieDrill.level < 3">
              <!-- R2-8: ขยาย viewBox แนวนอน (-10..50 → -12..56) กัน leader-line label ฝั่งขวา/ซ้ายถูก clip -->
              <svg class="pie-svg" width="600" height="600" viewBox="-12 -8 68 60" preserveAspectRatio="xMidYMid meet">
                <circle
                  cx="21" cy="21" r="15.915" fill="#ffffff"
                  :style="{ cursor: pieDrill.level > 1 ? 'pointer' : 'default' }"
                  @click="drillUp"
                ></circle>
                <g
                  v-for="(seg, si) in pieSegments"
                  :key="'seg-' + si"
                  class="pie-slice-clickable"
                  :style="{ cursor: seg.drillable ? 'pointer' : 'default' }"
                  @click="drillInto(seg.index)"
                >
                  <circle
                    cx="21" cy="21" r="15.915" fill="transparent"
                    :stroke="seg.colorHex" stroke-width="10"
                    :stroke-dasharray="seg.dash" :stroke-dashoffset="seg.offset"
                    transform="rotate(-90 21 21)" pointer-events="visibleStroke"
                  ></circle>
                  <line
                    v-if="seg.showLabel"
                    :x1="seg.lineX1" :y1="seg.lineY1" :x2="seg.lineX2" :y2="seg.lineY2"
                    :stroke="seg.labelColor" stroke-width="0.4" pointer-events="none"
                  ></line>
                  <text
                    v-if="seg.showLabel"
                    :x="seg.lx" :y="seg.ly" font-size="3.2" font-weight="bold"
                    :fill="seg.labelColor" :text-anchor="seg.anchor" dominant-baseline="middle"
                    pointer-events="none"
                  >{{ seg.pct }}%</text>
                  <text
                    v-if="seg.showLabel"
                    :x="seg.lx" :y="seg.nameY" font-size="1.6" font-weight="600"
                    :fill="seg.labelColor" :text-anchor="seg.anchor" pointer-events="none"
                  >{{ seg.name }}</text>
                </g>
              </svg>
              <div class="pie-legend">
                <div
                  class="pie-legend-item"
                  v-for="(sl, li) in pieViewSlices"
                  :key="'leg-' + li"
                  :class="{ clickable: sl.drillable }"
                  @click="drillInto(li)"
                >
                  <span class="pie-color-box" :style="{ background: sl.colorHex }"></span>
                  <strong>{{ formatPct(sl.percent) }}%</strong>
                  <span class="pie-legend-item-desc">{{ sl.name }} · {{ sl.count }} SKU<span v-if="sl.drillable" class="pie-legend-drill"> ▸</span></span>
                </div>
              </div>
              <div v-if="pieDrill.level === 1 && dashboard.pie.noDataSku > 0" class="pie-nodata-note">
                * {{ dashboard.pie.noDataSku }} SKU ไม่มีข้อมูล (ไม่นับเข้าสัดส่วน)
              </div>
            </div>

            <!-- level 3: ตารางรายสินค้า (ชื่อสินค้า | SCVD ปัจจุบัน) — ตรง mockup ② renderLevel3 -->
            <div v-else class="pie-l3" style="padding:6px 2px 2px;">
              <div style="font-size:12px;color:#374151;margin-bottom:6px;">
                🎯 <b>{{ pieDrillStatusName }}</b> · 📍 <b>{{ pieDrillPlantName }}</b> · <b>{{ pieLevel3Rows.length }} SKU</b>
              </div>
              <table style="width:100%;border-collapse:collapse;font-size:12px;">
                <thead>
                  <tr style="border-bottom:2px solid #e5e7eb;">
                    <th style="width:70%;text-align:left;padding:6px 4px;color:#6b7280;font-weight:600;">ชื่อสินค้า</th>
                    <th style="width:30%;text-align:right;padding:6px 4px;color:#6b7280;font-weight:600;">SCVD ปัจจุบัน (วัน)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!pieLevel3Rows.length">
                    <td colspan="2" style="text-align:center;color:#9ca3af;padding:20px;">ไม่มีสินค้าในสถานะนี้</td>
                  </tr>
                  <tr v-for="(row, ri) in pieLevel3Rows" :key="'l3-' + ri" style="border-bottom:1px solid #f3f4f6;">
                    <td style="padding:6px 4px;">🍺 {{ row.name }}</td>
                    <td style="padding:6px 4px;text-align:right;font-weight:700;" :style="{ color: pieDrillStatusColor }">{{ pieScvdText(row.scvd) }}</td>
                  </tr>
                </tbody>
              </table>
              <div style="font-size:11px;color:#9ca3af;margin-top:6px;">📊 เรียง SCVD น้อย → มาก (วิกฤตสุดขึ้นก่อน)</div>
            </div>
          </div>
        </div>

        <!-- ===== ③ BULLET CHART ===== -->
        <div class="widget">
          <div class="widget-header bullet-header-wrap">
            <div class="widget-title">📊 การเทียบข้อมูลตามหน้างานจริง vs ข้อมูลตามเป้าหมาย vs Target SCVD (จำลอง)</div>
            <div class="bullet-toolbar">
              <span class="widget-hint">💡 คลิกแถว → กรองข้อมูลทั้งหน้าเป็น SKU นั้น</span>
              <!-- QA BUG-07 (spec v3 R63): เรียง SCVD (ตามหน้างานจริง) ต่ำสุด/สูงสุด — เลือก 6 SKU ใหม่ฝั่ง server -->
              <!-- QA-07b (spec v3 R63 + mockup): กรองคลังเฉพาะกล่องนี้ · ตัวเลือก = คลังที่อยู่ในขอบเขตตัวกรองด้านบน -->
              <select class="form-control form-control-sm bullet-sort" v-model="bulletPlant" @change="loadDashboard">
                <option value="">คลัง: ทั้งหมด</option>
                <option v-for="bp in bulletPlantOptions" :key="'bp-' + bp.code" :value="bp.code">คลัง: {{ bp.name }}</option>
              </select>
              <select class="form-control form-control-sm bullet-sort" v-model="bulletSort" @change="loadDashboard">
                <option value="asc">เรียง: วิกฤตสุด → ล้นสุด</option>
                <option value="desc">เรียง: ล้นสุด → วิกฤตสุด</option>
              </select>
            </div>
          </div>

          <div v-if="!dashboard.bullet.length" class="scvd-empty-block">ไม่พบข้อมูลสำหรับกราฟเทียบเป้าหมาย</div>

          <div v-for="(grp, gi) in bulletByPlant" :key="'bwh-' + gi" class="bullet-warehouse">
            <div class="bullet-warehouse-title">
              <span>🏭 {{ grp.plantName }} — {{ monthName }} {{ filterYear }}</span>
            </div>

            <div v-for="(item, ii) in grp.items" :key="'brow-' + gi + '-' + ii" class="bullet-row" @click="cardClick(item)">
              <div class="bullet-header">
                <div class="bullet-sku-info">
                  <span class="bullet-sku">🍺 {{ item.productName }}</span>
                  <span class="bullet-badge" :style="{ background: tint(item.colorHex, 0.8), color: darken(item.colorHex, 0.35) }"><span class="status-swatch" :style="{ background: item.colorHex }"></span>{{ tierName(item.actualScvd) }}</span>
                </div>
                <div class="bullet-demand">
                  Demand: <strong>{{ fmt(item.demandPerDayLitre) }} L ({{ fmt(item.demandPerDayKeg) }} ถัง)/วัน</strong>
                  <span v-if="stockText(item)"> · Stock ปัจจุบัน: <strong>{{ stockText(item) }}</strong></span>
                </div>
              </div>

              <div class="bullet-chart-wrapper">
                <div class="bullet-chart">
                  <div
                    v-for="(z, zi) in bulletZones"
                    :key="'z-' + gi + '-' + ii + '-' + zi"
                    class="bullet-zone"
                    :style="{ width: z.widthPct + '%', background: tint(z.colorHex, 0.55) }"
                  >{{ z.label }}</div>

                  <template v-if="bulletPos(item.actualScvd) !== null">
                    <div class="bullet-actual" :style="{ width: bulletPos(item.actualScvd) + '%' }"></div>
                    <div class="bullet-marker-label actual" :style="{ left: bulletPos(item.actualScvd) + '%' }">▼ ตามหน้างานจริง {{ scvdDisplay(item.actualScvd) }} วัน</div>
                  </template>

                  <template v-if="bulletPos(item.targetScvd) !== null">
                    <div class="bullet-marker target-master" :style="{ left: bulletPos(item.targetScvd) + '%' }"></div>
                    <div class="bullet-marker-label target-master" :style="{ left: bulletPos(item.targetScvd) + '%' }">▼ ตามเป้าหมาย {{ scvdDisplay(item.targetScvd) }} วัน</div>
                  </template>

                  <template v-if="bulletPos(item.targetSimScvd) !== null">
                    <div class="bullet-marker target-scvd" :style="{ left: bulletPos(item.targetSimScvd) + '%' }"></div>
                    <div class="bullet-marker-label target-scvd" :style="{ left: bulletPos(item.targetSimScvd) + '%' }">▼ Target SCVD (จำลอง) {{ scvdDisplay(item.targetSimScvd) }} วัน</div>
                  </template>
                </div>
              </div>

              <div class="bullet-gap-box" :style="{ borderLeftColor: item.colorHex, background: tint(item.colorHex, 0.94) }">
                <div class="bullet-gap-title">📦 ส่วนต่างการวิเคราะห์ (3 รูปแบบ) <span class="bullet-gap-subnote">— BRD §4.4.6 · % วัดจาก Supply</span></div>
                <div class="bullet-gap-row" v-for="row in gapRows(item)" :key="'gap-' + gi + '-' + ii + '-' + row.num">
                  <span class="bullet-gap-label">
                    <span class="gap-num">{{ row.num }}</span>
                    <span :class="row.fromCls">{{ row.fromText }}</span> →
                    <span :class="row.toCls">{{ row.toText }}</span>
                  </span>
                  <span class="bullet-gap-value" :class="row.gap.cls">{{ row.gap.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== ④ HEATMAP ===== -->
        <div class="widget">
          <div class="widget-header">
            <div class="widget-title">🌡️ ตารางสถานะสต๊อก — ณ วันสิ้นเดือน (12 เดือน)</div>
            <div class="widget-hint">💡 คลิกช่อง → กรองข้อมูลเดือน + SKU</div>
          </div>

          <div v-if="!dashboard.heatmap.length" class="scvd-empty-block">ไม่พบข้อมูล heatmap</div>

          <div v-for="(plant, pi) in dashboard.heatmap" :key="'hm-' + pi" class="heatmap-warehouse">
            <div class="heatmap-warehouse-title">
              <span>🏭 {{ plant.plantName }}</span>
              <span class="heatmap-warehouse-meta">
                {{ plant.rows.length }} SKU ·
                Demand รวม {{ fmt(plant.demandTotalLitre) }} L ({{ fmt(plant.demandTotalKeg) }} ถัง)/วัน ·
                Supply รวม {{ fmt(plant.supplyTotalLitre) }} L ({{ fmt(plant.supplyTotalKeg) }} ถัง)/วัน
              </span>
            </div>
            <div class="heatmap-scroll">
              <table class="heatmap-table">
                <thead>
                  <tr>
                    <th class="sku-header">SKU</th>
                    <th
                      v-for="(mlbl, mi) in monthShort"
                      :key="'hmh-' + pi + '-' + mi"
                      :class="{ 'current-month': mi === currentMonthIndex }"
                    >{{ mlbl }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in plant.rows" :key="'hmr-' + pi + '-' + ri">
                    <td class="heatmap-sku-cell">🍺 {{ row.productName }}</td>
                    <td
                      v-for="(cell, mi) in row.months"
                      :key="'hmc-' + pi + '-' + ri + '-' + mi"
                      class="heatmap-cell"
                      :class="{ 'current-col': cell.isCurrentMonth }"
                      :style="cellStyle(cell)"
                      @click="cellClick(row, cell)"
                    >{{ scvdDisplay(cell.scvd) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="heatmap-legend">
            <div class="heatmap-legend-item" v-for="lv in criteriaLevels" :key="'hleg-' + lv.order">
              <span class="heatmap-legend-swatch" :style="{ background: lv.colorHex }"></span>{{ badgeLabel(lv) }} วัน
            </div>
            <div class="heatmap-legend-item" style="margin-left:16px;">
              <span class="heatmap-legend-swatch" style="background:#FFDB00;"></span>เดือนปัจจุบัน
            </div>
          </div>
        </div>
      </div>

      <div v-else class="scvd-empty-block" style="margin:30px;">ไม่พบข้อมูล — กรุณาปรับตัวกรอง</div>

      <!-- ============ ⑤ FORMULA MODAL ============ -->
      <div v-if="showFormulaModal" class="scvd-modal-overlay" @click.self="showFormulaModal = false">
        <div class="scvd-modal">
          <div class="scvd-modal-header">
            <div>
              <h2>📐 สูตรคำนวณที่ใช้ใน Dashboard</h2>
              <div class="modal-subtitle">อธิบายที่มาของทุกตัวเลข — ใช้ตอบคำถามลูกค้าและทีม Support</div>
            </div>
            <button class="scvd-modal-close" @click="showFormulaModal = false">✕</button>
          </div>
          <div class="scvd-modal-body">

            <!-- 📌 Table of Contents -->
            <div class="modal-toc">
              <div class="modal-toc-title">📌 สารบัญ (คลิกเพื่อกระโดดไปยังหัวข้อ)</div>
              <div class="modal-toc-list">
                <a class="modal-toc-item" href="#widget-alert" @click.prevent="scrollToWidget('widget-alert')">🚨 สินค้าที่ต้องดำเนินการเร่งด่วน</a>
                <a class="modal-toc-item" href="#widget-pie" @click.prevent="scrollToWidget('widget-pie')">🥧 สัดส่วนสถานะสต็อก 5 ระดับ</a>
                <a class="modal-toc-item" href="#widget-bullet" @click.prevent="scrollToWidget('widget-bullet')">📊 การเทียบข้อมูล... (Bullet Chart)</a>
                <a class="modal-toc-item" href="#widget-heatmap" @click.prevent="scrollToWidget('widget-heatmap')">🌡️ ตารางสถานะสต๊อก 12 เดือน</a>
                <a class="modal-toc-item" href="#widget-common" @click.prevent="scrollToWidget('widget-common')" style="grid-column:1/-1;">⚙️ หลักการคำนวณกลาง (ใช้ทุกกล่อง)</a>
              </div>
            </div>

            <!-- ============ Widget 1: Alert Board ============ -->
            <div class="widget-group" id="widget-alert">
              <div class="widget-group-title">🚨 กล่อง "สินค้าที่ต้องดำเนินการเร่งด่วน"</div>
              <div class="widget-group-body">

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">1</span>SCVD (ตามหน้างานจริง) ปัจจุบัน — ตัวเลขใหญ่</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ตัวเลขใหญ่ทางซ้ายในแต่ละการ์ด</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริงจากหน้าจอ (เบียร์สดสิงห์ 15L โรงงานปทุมธานี)</div>
                    <div class="visual-mockup" style="max-width:340px;">
                      <div style="font-size:9px; background:#fde6e6; color:#b04747; padding:2px 6px; border-radius:3px; display:inline-block; font-weight:700;">🟥 ขาดสต๊อก</div>
                      <div style="font-weight:700; margin-top:4px;">🍺 เบียร์สดสิงห์ 15 ลิตร</div>
                      <div style="color:#6b7280; font-size:10px;">🏭 โรงงานปทุมธานี</div>
                      <div style="margin-top:6px; display:flex; align-items:baseline; gap:4px;">
                        <span class="visual-highlight" style="font-size:22px;">2</span>
                        <span style="color:#6b7280; font-size:11px;">วัน</span>
                        <span class="visual-arrow">← ตัวเลขนี้</span>
                      </div>
                      <div style="font-size:9px; color:#9ca3af; font-style:italic;">SCVD ปัจจุบัน</div>
                    </div>
                  </div>
                  <div class="formula-code">SCVD (ตามหน้างานจริง) = BG Stock (ตามหน้างานจริง) ÷ Demand (ตามหน้างานจริง) เฉลี่ย/วัน

โดย:
  BG Stock (ตามหน้างานจริง) = BG Stock ต้นเดือน (ตามหน้างานจริง)
                            + Supply (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                            − Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน

  Demand (ตามหน้างานจริง) เฉลี่ย/วัน = Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                                      ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
                                       × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, วันที่ 15 ก.ค. 2026, ขนาดถัง 15 L):</b><br>
                    Step 1: จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = 26 × (15÷31) = 12.58 ≈ <b>13 วัน</b><br>
                    Step 2: Demand (ตามหน้างานจริง) เฉลี่ย/วัน = 845 L ÷ 13 = <b>65 L/วัน</b><br>
                    Step 3: BG Stock (ตามหน้างานจริง) = 750 + 195 − 845 = <b>100 L</b><br>
                    Step 4: SCVD (ตามหน้างานจริง) = 100 ÷ 65 = 1.54 ≈ <b>2 วัน</b> (ปัดขึ้นเพราะ 0.54 ≥ 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • BG Stock ต้นเดือน (ตามหน้างานจริง) = ค่าคงเหลือ ณ วันสุดท้ายของเดือนก่อน<br>
                    • Supply (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ผลิตเบียร์สด" (#02) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • Demand (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ส่งสินค้าไปยังตัวแทน" (#04) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">2</span>Demand (ตามหน้างานจริง) เฉลี่ย/วัน — บรรทัด "Demand"</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> บรรทัด "Demand" ทางขวาของการ์ด</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="text-align:right; font-size:10.5px; color:#6b7280;">— จากข้อมูลตามหน้างานจริง —<br>
                        Demand <span class="visual-highlight">65 L (4 ถัง)</span>/วัน <span class="visual-arrow">← ตัวเลขนี้</span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">Demand (ตามหน้างานจริง) เฉลี่ย/วัน = Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                                    ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

โดย:
  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
                                       × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, วันที่ 15 ก.ค. 2026):</b><br>
                    Step 1: จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = 26 วัน (ทีมกลยุทธ์กรอก)<br>
                    Step 2: จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = 26 × (15÷31) = 12.58 ≈ <b>13 วัน</b><br>
                    Step 3: Demand (ตามหน้างานจริง) สะสม 1-15 ก.ค. = <b>845 L</b><br>
                    Step 4: Demand (ตามหน้างานจริง) เฉลี่ย/วัน = 845 ÷ 13 = <b>65 L/วัน</b><br>
                    Step 5: แปลงเป็นถัง = 65 ÷ 15 (ขนาดถังสิงห์ 15L) = 4.33 ≈ <b>4 ถัง/วัน</b> (ปัดลง 0.33 &lt; 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Demand (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ส่งสินค้าไปยังตัวแทน" (#04) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">3</span>Supply (ตามหน้างานจริง) เฉลี่ย/วัน — บรรทัด "Supply"</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> บรรทัด "Supply" ทางขวาของการ์ด</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="text-align:right; font-size:10.5px; color:#6b7280;">Demand 65 L (4 ถัง)/วัน<br>
                        Supply <span class="visual-highlight">15 L (1 ถัง)</span>/วัน <span class="visual-arrow">← ตัวเลขนี้</span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">Supply (ตามหน้างานจริง) เฉลี่ย/วัน = Supply (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                                    ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

โดย:
  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
                                       × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, วันที่ 15 ก.ค. 2026):</b><br>
                    Step 1: จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = 26 × (15÷31) = 12.58 ≈ <b>13 วัน</b><br>
                    Step 2: Supply (ตามหน้างานจริง) สะสม 1-15 ก.ค. = <b>195 L</b><br>
                    Step 3: Supply (ตามหน้างานจริง) เฉลี่ย/วัน = 195 ÷ 13 = <b>15 L/วัน</b><br>
                    Step 4: แปลงเป็นถัง = 15 ÷ 15 (ขนาดถังสิงห์ 15L) = <b>1 ถัง/วัน</b></div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Supply (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ผลิตเบียร์สด" (#02) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">4</span>แนวโน้มเทียบเดือนก่อน (▼ ▲ ━)</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> มุมขวาบนของแต่ละการ์ด</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup" style="max-width:340px;">
                      <div style="display:flex; justify-content:space-between; align-items:center; font-size:10px;">
                        <span style="background:#fde6e6; color:#b04747; padding:2px 6px; border-radius:3px; font-weight:700;">🟥 ขาดสต๊อก</span>
                        <span class="visual-highlight" style="font-size:11px; color:#b04747;">▼ 5 วัน จากเดือนก่อน</span>
                        <span class="visual-arrow" style="margin-left:0;">← ตัวเลขนี้</span>
                      </div>
                      <div style="margin-top:4px; font-weight:700; font-size:11px;">🍺 เบียร์สดสิงห์ 15 ลิตร</div>
                    </div>
                  </div>
                  <div class="formula-code">แนวโน้ม = SCVD (ตามหน้างานจริง) เดือนนี้
        − SCVD (ตามหน้างานจริง) เดือนที่แล้ว

(ใช้ค่า SCVD ณ วันสุดท้ายของเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, เทียบ ก.ค. vs มิ.ย. 2026):</b><br>
                    Step 1: SCVD (ตามหน้างานจริง) มิ.ย. 2026 ณ วันสิ้นเดือน = <b>7 วัน</b><br>
                    Step 2: SCVD (ตามหน้างานจริง) ก.ค. 2026 ปัจจุบัน = <b>2 วัน</b><br>
                    Step 3: แนวโน้ม = 2 − 7 = <b>−5</b> → แสดง <b>▼ 5 วัน จากเดือนก่อน</b><br><br>
                    <b>สัญลักษณ์:</b><br>
                    • <b>▼ ลดลง</b> = สต๊อกลดลง (แย่ลง — ต้องผลิตเพิ่ม)<br>
                    • <b>▲ เพิ่มขึ้น</b> = สต๊อกเพิ่มขึ้น (ดีขึ้น)<br>
                    • <b>━ คงที่</b> = ไม่เปลี่ยนแปลง</div>
                  <div class="formula-source">🗂️ <b>ที่มา:</b> SCVD (ตามหน้างานจริง) เดือนก่อน = คำนวณจาก BG Stock/Supply/Demand ณ วันสุดท้ายของเดือนก่อน (สูตรเดียวกับข้อ (1) SCVD ปัจจุบัน แต่ใช้ข้อมูลปิดเดือน)</div>
                </div>

              </div>
            </div>

            <!-- ============ Widget 2: Pie Chart ============ -->
            <div class="widget-group" id="widget-pie">
              <div class="widget-group-title">🥧 กล่อง "สัดส่วนสถานะสต็อก 5 ระดับ"</div>
              <div class="widget-group-body">
                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">1</span>% และจำนวน SKU ในแต่ละสถานะ</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ในแผนภูมิพายและ Legend ด้านล่าง</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="font-size:11px; line-height:1.7;">
                        🟥 <span class="visual-highlight">30%</span> ขาดสต๊อก · <span class="visual-highlight">15 SKU</span><br>
                        🟧 20% สต็อกใกล้หมด · 10 SKU<br>
                        🟩 35% บริหารดี · 18 SKU<br>
                        🟨 10% สต็อกเริ่มสูง · 5 SKU<br>
                        🟪 5% สต็อกล้น · 2 SKU
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">% = จำนวน SKU ที่มี SCVD (ตามหน้างานจริง) อยู่ในสถานะนั้น
    ÷ จำนวน SKU รวมทั้งหมดในระบบ
    × 100

โดย: สถานะของ SKU ถูกจัดตามค่า SCVD (ตามหน้างานจริง) ปัจจุบัน เทียบกับเกณฑ์ 5 ระดับ</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ:</b><br>
                    Step 1: นับจำนวน SKU ที่มี SCVD (ตามหน้างานจริง) อยู่ในช่วง 0-3 วัน = <b>15 SKU</b> (สถานะ "ขาดสต๊อก")<br>
                    Step 2: จำนวน SKU รวมทั้งหมดในระบบ = <b>50 SKU</b><br>
                    Step 3: % = 15 ÷ 50 × 100 = <b>30%</b></div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • SCVD (ตามหน้างานจริง) ของแต่ละ SKU = คำนวณจากสูตรใน Widget 1 หัวข้อ (1) — real-time<br>
                    • เกณฑ์ 5 ระดับ = ตั้งค่าในหน้าจอ "แผงตั้งค่าเกณฑ์การแสดงผล" (Setup Panel) — แอดมิน Stock Cover Days ปรับได้<br>
                    • จำนวน SKU รวมทั้งหมด = จำนวนช่อง (SKU × คลัง) ที่แสดงในตาราง — จะแสดงเมื่อ<b>ทั้ง "คลัง" และ "สินค้า"</b>นั้นเปิดใช้งาน "ใช้งาน Stock Cover Days" (ดูรายละเอียดที่หน้า "จัดการข้อมูลคลังสินค้า" + "จัดการข้อมูลสินค้า")</div>
                </div>
              </div>
            </div>

            <!-- ============ Widget 3: Bullet Chart ============ -->
            <div class="widget-group" id="widget-bullet">
              <div class="widget-group-title">📊 กล่อง "การเทียบข้อมูลตามหน้างานจริง vs ข้อมูลตามเป้าหมาย vs Target SCVD (จำลอง)"</div>
              <div class="widget-group-body">

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">1</span>BG Stock (ตามหน้างานจริง) ปัจจุบัน — บรรทัด "Stock ปัจจุบัน"</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> บรรทัด "Stock ปัจจุบัน:" ทางขวาบนของแต่ละ SKU</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง (เบียร์สดสิงห์ 15L โรงงานปทุมธานี)</div>
                    <div class="visual-mockup">
                      <div style="font-size:11px;">🍺 <b>เบียร์สดสิงห์ 15 ลิตร</b> 🟥 ขาดสต๊อก</div>
                      <div style="font-size:10.5px; color:#6b7280; margin-top:4px;">Demand: 65 L (4 ถัง)/วัน · Stock ปัจจุบัน: <span class="visual-highlight">100 L (7 ถัง)</span> <span class="visual-arrow">← ตัวเลขนี้</span></div>
                    </div>
                  </div>
                  <div class="formula-code">BG Stock (ตามหน้างานจริง) ปัจจุบัน = BG Stock ต้นเดือน (ตามหน้างานจริง)
                                    + Supply (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                                    − Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, วันที่ 15 ก.ค. 2026, ขนาดถัง 15 L):</b><br>
                    Step 1: BG Stock ต้นเดือน (ตามหน้างานจริง) ณ 1 ก.ค. = <b>750 L</b><br>
                    Step 2: Supply (ตามหน้างานจริง) สะสม 1-15 ก.ค. = <b>195 L</b><br>
                    Step 3: Demand (ตามหน้างานจริง) สะสม 1-15 ก.ค. = <b>845 L</b><br>
                    Step 4: BG Stock (ตามหน้างานจริง) ปัจจุบัน = 750 + 195 − 845 = <b>100 L</b><br>
                    Step 5: แปลงเป็นถัง = 100 ÷ 15 (ขนาดถัง) = 6.67 ≈ <b>7 ถัง</b> (ปัดขึ้นเพราะ 0.67 ≥ 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • BG Stock ต้นเดือน (ตามหน้างานจริง) = ค่า BG Stock (ตามหน้างานจริง) ณ วันสุดท้ายของเดือนก่อน (คำนวณต่อเนื่อง)<br>
                    • Supply (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ผลิตเบียร์สด" (#02) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • Demand (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ส่งสินค้าไปยังตัวแทน" (#04) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • คำนวณ real-time (อัปเดตภายใน 5 วินาที เมื่อมีใบงาน #02 หรือ #04 ปิดเพิ่ม)</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">2</span>Demand (ตามหน้างานจริง) เฉลี่ย/วัน — บรรทัด "Demand"</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> บรรทัด "Demand:" ทางขวาบนของแต่ละ SKU</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="font-size:10.5px; color:#6b7280;">Demand: <span class="visual-highlight">65 L (4 ถัง)</span>/วัน <span class="visual-arrow">← ตัวเลขนี้</span> · Stock ปัจจุบัน: 100 L (7 ถัง)</div>
                    </div>
                  </div>
                  <div class="formula-code">Demand (ตามหน้างานจริง) เฉลี่ย/วัน = Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน
                                    ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

โดย:
  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
                                       × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, วันที่ 15 ก.ค. 2026):</b><br>
                    Step 1: จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = 26 วัน<br>
                    Step 2: จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = 26 × (15÷31) = 12.58 ≈ <b>13 วัน</b><br>
                    Step 3: Demand (ตามหน้างานจริง) สะสม 1-15 ก.ค. = <b>845 L</b><br>
                    Step 4: Demand (ตามหน้างานจริง) เฉลี่ย/วัน = 845 ÷ 13 = <b>65 L/วัน</b><br>
                    Step 5: แปลงเป็นถัง = 65 ÷ 15 = 4.33 ≈ <b>4 ถัง/วัน</b> (ปัดลง 0.33 &lt; 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Demand (ตามหน้างานจริง) สะสม = ผลรวมใบงาน "ส่งสินค้าไปยังตัวแทน" (#04) ที่ปิดเสร็จตั้งแต่วันที่ 1 ของเดือน<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">3</span>ป้ายเป้าหมาย 2 จุดบนกราฟ</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> เส้น marker ตั้งฉากบนกราฟแท่ง</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="font-size:11px; line-height:2;">
                        <span style="background:#1d4ed8; color:white; padding:3px 8px; border-radius:3px; font-size:10px; font-weight:700;">▼ SCVD (ตามเป้าหมาย) 14</span> = ค่าเป้าที่กำหนดในเดือน<br>
                        <span style="background:#7e22ce; color:white; padding:3px 8px; border-radius:3px; font-size:10px; font-weight:700;">▼ Target SCVD (จำลอง) 18</span> = ค่าจำลองจาก Setup Panel
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">SCVD (ตามเป้าหมาย) = ค่าที่ทีมกลยุทธ์กำหนดล่วงหน้าต่อเดือน
                                (ไม่ใช่สูตรคำนวณ — เป็นค่าที่กรอกเข้าระบบโดยตรง)

Target SCVD (จำลอง)   = ค่าเป้าหมายที่แอดมิน Stock Cover Days ตั้งเพื่อจำลอง scenario
                                (ไม่ใช่สูตรคำนวณ — เป็นค่าที่ตั้งใน Setup Panel)</div>
                  <div class="formula-example"><b>ตัวอย่าง (เบียร์สดสิงห์ 15L, ก.ค. 2026):</b><br>
                    • SCVD (ตามเป้าหมาย) ก.ค. 2026 = <b>14 วัน</b> (ทีมกลยุทธ์ตั้งไว้ตอนต้นปี)<br>
                    • Target SCVD (จำลอง) = <b>18 วัน</b> (แอดมินตั้งเพื่อจำลอง "ถ้าเราต้องการปลอดภัยกว่านี้")</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • SCVD (ตามเป้าหมาย): กรอกโดยทีมกลยุทธ์ในหน้า "จัดการข้อมูล Stock Cover Days" (คอลัมน์ SCVD ในแต่ละเดือน × คลัง × สินค้า)<br>
                    • Target SCVD (จำลอง): ตั้งค่าจากหน้า Setup Panel — เข้าจากปุ่ม "⚙️ แก้ไขเกณฑ์" ในแถบเกณฑ์ (เฉพาะแอดมิน Stock Cover Days เท่านั้นที่บันทึกตั้งเป็นค่าเริ่มต้นได้ 🔒)</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">4</span>ส่วนต่างการวิเคราะห์ (3 รูปแบบ)</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> กล่อง "🚨 ส่วนต่างการวิเคราะห์ (3 รูปแบบ)" ใต้แต่ละ SKU</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง (เบียร์สดสิงห์ 15L)</div>
                    <div class="visual-mockup">
                      <div style="background:#fef2f2; padding:8px; border-left:3px solid #FF6B6B; border-radius:4px; font-size:10.5px; line-height:1.7;">
                        <b>🚨 ส่วนต่างการวิเคราะห์ (3 รูปแบบ)</b><br>
                        <span class="gap-num">1</span> ตามหน้างานจริง → ตามเป้าหมาย: <span class="visual-highlight">🔻 ห่างจากเป้าหมาย 86.1% — ต้องผลิตเพิ่ม 1,205 L (80 ถัง)</span><br>
                        <span class="gap-num">2</span> ตามหน้างานจริง → Target SCVD (จำลอง): 🔻 ห่างจากเป้าหมาย 88.9% — ต้องผลิตเพิ่ม 800 L (53 ถัง)<br>
                        <span class="gap-num">3</span> ตามเป้าหมาย → Target SCVD (จำลอง): 🔻 ห่างจากเป้าหมาย 22.2% — ต้องปรับเพิ่มแผนผลิต 200 L (13 ถัง)
                      </div>
                    </div>
                  </div>
                  <div style="background:#fefce8; border-left:3px solid #eab308; padding:8px 12px; border-radius:5px; margin:8px 0; font-size:11.5px; color:#78350f;">
                    💼 <b>ข้อมูลตั้งต้นสำหรับตัวอย่างทั้ง 3 รูปแบบ</b> — เบียร์สดสิงห์ 15L (ขนาดถัง 15 L), วันที่ 15 ก.ค. 2026<br>
                    <span style="display:inline-block; margin-top:4px; padding-left:14px;">
                      • <b>ตามหน้างานจริง:</b> BG Stock ปัจจุบัน = 100 L · Demand สะสม = 845 L · Supply สะสม = 195 L<br>
                      • <b>ตามเป้าหมาย:</b> Demand เต็มเดือน = 1,300 L · Supply เต็มเดือน = 1,400 L · BG Stock ต้นเดือน = 700 L<br>
                      • <b>Target SCVD (จำลอง)</b> = 18 วัน · <b>จำนวนวันขาย (ตามเป้าหมาย)</b> = 26 วัน
                    </span>
                  </div>

                  <div class="gap-forms-container">

                    <!-- รูปแบบ 1 -->
                    <div class="gap-form-card gap-form-1">
                      <div class="gap-form-header">🔴 รูปแบบ 1 <span class="gap-form-badge">ตามหน้างานจริง → ตามเป้าหมาย</span></div>
                      <div class="gap-form-question">💬 "เดือนนี้ต้องเร่งอีกเท่าไหร่ ถึงจะทันแผนทีมกลยุทธ์"</div>
                      <div class="gap-form-formula">ส่วนต่าง Demand = Demand (ตามเป้าหมาย) เต็มเดือน
                − Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน

ส่วนต่าง Supply = Supply (ตามเป้าหมาย) เต็มเดือน
                − Supply (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน

%             = ส่วนต่าง Supply ÷ Supply (ตามเป้าหมาย) เต็มเดือน × 100</div>
                      <div class="gap-form-example">
                        🎯 <b>ตัวอย่างการคำนวณ:</b><br>
                        &nbsp;&nbsp;&nbsp;<b>ส่วนต่าง Demand</b> = 1,300 − 845 = <b>455 L</b> → ÷15 = <b>30 ถัง</b><br>
                        &nbsp;&nbsp;&nbsp;<i style="color:#6b7280; font-size:11px;">(ตลาดยังจะสั่งอีก 30 ถัง ก่อนสิ้นเดือน)</i><br>
                        &nbsp;&nbsp;&nbsp;<b>ส่วนต่าง Supply</b> ⭐ = 1,400 − 195 = <b>1,205 L</b> → ÷15 = <b>80 ถัง</b><br>
                        &nbsp;&nbsp;&nbsp;<i style="color:#6b7280; font-size:11px;">(ต้องผลิตเพิ่ม 80 ถัง เพื่อทันแผน)</i><br>
                        &nbsp;&nbsp;&nbsp;<b>%</b> = 1,205 ÷ 1,400 × 100 = <b>86.1%</b>
                      </div>
                      <div style="background:#fef3c7; border-left:3px solid #f59e0b; padding:9px 12px; margin-top:10px; border-radius:4px; font-size:11px; color:#78350f; line-height:1.6;">
                        💡 <b>ทำไม % เทียบจาก Supply ไม่ใช่ Demand:</b> เพราะเราควบคุมการผลิต (Supply) ได้ แต่ควบคุมความต้องการตลาด (Demand) ไม่ได้ — Supply คือ <b>action number</b> ที่ทีมโรงงานลงมือทำได้จริง / Demand เป็น <b>ข้อมูลประกอบ</b> ให้เห็นแนวโน้มตลาด
                      </div>
                    </div>

                    <!-- รูปแบบ 2 -->
                    <div class="gap-form-card gap-form-2">
                      <div class="gap-form-header">🟣 รูปแบบ 2 <span class="gap-form-badge">ตามหน้างานจริง → Target SCVD (จำลอง)</span></div>
                      <div class="gap-form-question">💬 "อยากได้ SCVD = Target ต้องเร่งผลิต Supply เพิ่มอีกเท่าไหร่"</div>
                      <div class="gap-form-formula">BG Stock ที่ต้องมี = Target SCVD × Demand (ตามเป้าหมาย) เต็มเดือน
                   ÷ จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน

Supply ต้องเร่ง   = BG Stock ที่ต้องมี
                   − BG Stock (ตามหน้างานจริง) ปัจจุบัน

%                = Supply ต้องเร่ง ÷ BG Stock ที่ต้องมี × 100

(ไม่แสดง Demand — ควบคุมความต้องการตลาดไม่ได้)</div>
                      <div class="gap-form-example">
                        🎯 <b>ตัวอย่างการคำนวณ:</b><br>
                        &nbsp;&nbsp;&nbsp;BG Stock ที่ต้องมี = 18 × 1,300 ÷ 26 = <b>900 L</b><br>
                        &nbsp;&nbsp;&nbsp;Supply ต้องเร่ง = 900 − 100 = <b>800 L</b> → ÷15 = <b>53 ถัง</b> (53.33 ปัดลง)<br>
                        &nbsp;&nbsp;&nbsp;% = 800 ÷ 900 × 100 = <b>88.9%</b>
                      </div>
                    </div>

                    <!-- รูปแบบ 3 -->
                    <div class="gap-form-card gap-form-3">
                      <div class="gap-form-header">🔵 รูปแบบ 3 <span class="gap-form-badge">ตามเป้าหมาย → Target SCVD (จำลอง)</span></div>
                      <div class="gap-form-question">💬 "ทีมกลยุทธ์ต้องปรับ Supply ในแผนอีกเท่าไหร่ ถึงจะเข้า Target"</div>
                      <div class="gap-form-formula">BG Stock ที่ต้องมี = Target SCVD × Demand (ตามเป้าหมาย) เต็มเดือน
                   ÷ จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน

Supply ต้องปรับ  = BG Stock ที่ต้องมี
                   − BG Stock ต้นเดือน (ตามเป้าหมาย)

%                = Supply ต้องปรับ ÷ BG Stock ที่ต้องมี × 100</div>
                      <div class="gap-form-example">
                        🎯 <b>ตัวอย่างการคำนวณ:</b><br>
                        &nbsp;&nbsp;&nbsp;BG Stock ที่ต้องมี = 18 × 1,300 ÷ 26 = <b>900 L</b><br>
                        &nbsp;&nbsp;&nbsp;Supply ต้องปรับ = 900 − 700 = <b>200 L</b> → ÷15 = <b>13 ถัง</b> (13.33 ปัดลง)<br>
                        &nbsp;&nbsp;&nbsp;% = 200 ÷ 900 × 100 = <b>22.2%</b>
                      </div>
                    </div>

                  </div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Demand/Supply (ตามเป้าหมาย) เต็มเดือน = ทีมกลยุทธ์กรอกล่วงหน้าในหน้า "จัดการข้อมูล Stock Cover Days"<br>
                    • BG Stock ต้นเดือน (ตามเป้าหมาย) = ทีมกลยุทธ์กำหนดค่าเริ่มต้นในหน้า "จัดการข้อมูล Stock Cover Days"<br>
                    • Demand/Supply (ตามหน้างานจริง) สะสม = ผลรวมใบงาน #04 / #02 ที่ปิดเสร็จ<br>
                    • BG Stock (ตามหน้างานจริง) ปัจจุบัน = ดูสูตรใน หัวข้อ (1) BG Stock ปัจจุบัน<br>
                    • Target SCVD (จำลอง) = ตั้งค่าใน Setup Panel<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"</div>
                  <div class="formula-important">⚠️ <b>Note ทางธุรกิจ:</b> "ส่วนต่าง Supply" แต่ละรูปแบบมีความหมายต่างกัน — ห้ามใช้สลับกัน<br>
                    • รูปแบบ 1 = "ยังต้องผลิตอีกเท่าไหร่ ให้ทันแผนที่ทีมกลยุทธ์ตั้ง"<br>
                    • รูปแบบ 2 = "ยังต้องเร่งผลิตเพิ่มเท่าไหร่ ให้ SCVD ปัจจุบัน = Target ที่ตั้งจำลอง"<br>
                    • รูปแบบ 3 = "แผนของทีมกลยุทธ์ห่างจาก Target เท่าไหร่ (ต้องปรับแผนล่วงหน้า)"</div>
                </div>

              </div>
            </div>

            <!-- ============ Widget 4: Heatmap ============ -->
            <div class="widget-group" id="widget-heatmap">
              <div class="widget-group-title">🌡️ กล่อง "ตารางสถานะสต๊อก — ณ วันสิ้นเดือน (12 เดือน)"</div>
              <div class="widget-group-body">

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">1</span>SCVD (ตามหน้างานจริง) ในแต่ละช่องของตาราง</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ทุกช่องในตาราง (12 เดือน × จำนวน SKU)</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="display:grid; grid-template-columns:100px repeat(3,50px); gap:3px; font-size:10.5px; align-items:center;">
                        <div style="font-weight:600;">🍺 สิงห์ 15L</div>
                        <div style="background:#90D8B2; padding:6px; border-radius:3px; text-align:center; font-weight:700;">10</div>
                        <div style="background:#FF6B6B; color:white; padding:6px; border-radius:3px; text-align:center; font-weight:700;">2</div>
                        <div style="background:#FFB080; padding:6px; border-radius:3px; text-align:center; font-weight:700;">5</div>
                      </div>
                      <div style="margin-top:6px; font-size:10.5px; color:#dc2626; font-style:italic;">← ตัวเลขในทุกช่องคือ SCVD (ตามหน้างานจริง) ณ วันสิ้นเดือน</div>
                    </div>
                  </div>
                  <div class="formula-code">SCVD (ตามหน้างานจริง) ในช่องตาราง = BG Stock (ตามหน้างานจริง) ÷ Demand (ตามหน้างานจริง) เฉลี่ย/วัน

โดย:
  • เดือนที่ผ่านมา   → ใช้ค่า SCVD (ตามหน้างานจริง) ณ วันสุดท้ายของเดือนนั้น (ค่านิ่ง)
  • เดือนปัจจุบัน   → ใช้ค่า SCVD (ตามหน้างานจริง) ปัจจุบัน (real-time)
  • เดือนล่วงหน้า   → ไม่แสดงตัวเลข (ยังไม่มีข้อมูล)

  BG Stock (ตามหน้างานจริง) = BG Stock ต้นเดือน (ตามหน้างานจริง)
                            + Supply (ตามหน้างานจริง) สะสมในเดือนนั้น
                            − Demand (ตามหน้างานจริง) สะสมในเดือนนั้น

  Demand (ตามหน้างานจริง) เฉลี่ย/วัน = Demand (ตามหน้างานจริง) สะสมในเดือนนั้น
                                      ÷ จำนวนวันขาย (ตามเป้าหมาย) ในเดือนนั้น</div>
                  <div class="formula-example"><b>ตัวอย่าง (สิงห์ 15L โรงงานปทุมธานี, เดือน มิ.ย. 2026 ปิดเดือนแล้ว):</b><br>
                    Step 1: BG Stock ต้นเดือน (ตามหน้างานจริง) มิ.ย. = 900 L<br>
                    Step 2: Supply (ตามหน้างานจริง) มิ.ย. ทั้งเดือน = 1,400 L<br>
                    Step 3: Demand (ตามหน้างานจริง) มิ.ย. ทั้งเดือน = 1,350 L<br>
                    Step 4: BG Stock (ตามหน้างานจริง) ณ 30 มิ.ย. = 900 + 1,400 − 1,350 = <b>950 L</b><br>
                    Step 5: จำนวนวันขาย (ตามเป้าหมาย) มิ.ย. = 25 วัน<br>
                    Step 6: Demand เฉลี่ย/วัน = 1,350 ÷ 25 = <b>54 L/วัน</b><br>
                    Step 7: SCVD = 950 ÷ 54 = 17.6 ≈ <b>18 วัน</b> (ปัดขึ้น 0.6 ≥ 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มา:</b> ทุกตัวแปรใช้แหล่งเดียวกับหัวข้อ (1) SCVD ปัจจุบัน ในกล่อง "สินค้าที่ต้องดำเนินการเร่งด่วน"<br>
                    <b>สีของช่อง:</b> ยึดตามเกณฑ์ 5 ระดับ (ตั้งใน Setup Panel) — เหมือน Pie Chart</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">2</span>Demand (ตามหน้างานจริง) รวมของคลัง — ต่อวัน</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ข้อมูลข้างชื่อคลัง (ทางขวาของ 🏭)</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="display:flex; justify-content:space-between; align-items:center; padding-bottom:6px; border-bottom:2px solid #FFDB00; font-size:11px;">
                        <span>🏭 <b>โรงงานปทุมธานี</b></span>
                        <span style="color:#6b7280;">8 SKU · Demand รวม <span class="visual-highlight">320 L (13 ถัง)</span>/วัน · Supply รวม 220 L (9 ถัง)/วัน</span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">Demand (ตามหน้างานจริง) รวม/วัน (ลิตร) = Σ  Demand (ตามหน้างานจริง) เฉลี่ย/วัน ของทุก SKU ในคลัง

Demand (ตามหน้างานจริง) รวม/วัน (ถัง) = Σ  ( Demand (ตามหน้างานจริง) เฉลี่ย/วัน ของ SKU
                                            ÷ ขนาดถังของ SKU นั้น )

โดย:
  Demand (ตามหน้างานจริง) เฉลี่ย/วัน ของแต่ละ SKU
       = Demand (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน ของ SKU นั้น
         ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา
       = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่าง (โรงงานปทุมธานี, 3 SKU, วันที่ 15 ก.ค. 2026):</b><br>
                    สิงห์ 15L: Demand เฉลี่ย 45 L/วัน → 45÷15 = 3 ถัง<br>
                    สิงห์ 30L: Demand เฉลี่ย 150 L/วัน → 150÷30 = 5 ถัง<br>
                    สิงห์ 50L: Demand เฉลี่ย 125 L/วัน → 125÷50 = 2.5 ≈ 3 ถัง (ปัดขึ้น)<br>
                    <b>รวม (ลิตร)</b> = 45 + 150 + 125 = <b>320 L/วัน</b><br>
                    <b>รวม (ถัง)</b> = 3 + 5 + 3 = <b>13 ถัง/วัน</b> (แปลงต่อ SKU แล้วรวม)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Demand (ตามหน้างานจริง) สะสมของแต่ละ SKU = ใบงาน "ส่งสินค้าไปยังตัวแทน" (#04) ที่ปิดเสร็จ<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"<br>
                    • ขนาดถังของแต่ละ SKU = ดึงจากตาราง Master_Product ช่อง NETVOLUME</div>
                  <div class="formula-important">⚠️ <b>ห้าม</b> คำนวณ "ถังรวม" โดยเอา "ลิตรรวม" หารด้วย 30 ก้อนใหญ่ — เบียร์แต่ละขนาดถังต่างกัน (15/20/22.7/22.9/25/30/50 L) จะคลาดเคลื่อน 20-40% <b>ต้องแปลงทีละสินค้าก่อนแล้วค่อยรวม</b></div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">3</span>Supply (ตามหน้างานจริง) รวมของคลัง — ต่อวัน</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ข้อมูลข้างชื่อคลัง (บรรทัดเดียวกับ Demand รวม)</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจริง</div>
                    <div class="visual-mockup">
                      <div style="display:flex; justify-content:space-between; align-items:center; padding-bottom:6px; border-bottom:2px solid #FFDB00; font-size:11px;">
                        <span>🏭 <b>โรงงานปทุมธานี</b></span>
                        <span style="color:#6b7280;">Demand รวม 320 L (13 ถัง)/วัน · Supply รวม <span class="visual-highlight">220 L (9 ถัง)</span>/วัน <span class="visual-arrow">← ตัวเลขนี้</span></span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">Supply (ตามหน้างานจริง) รวม/วัน (ลิตร) = Σ  Supply (ตามหน้างานจริง) เฉลี่ย/วัน ของทุก SKU ในคลัง

Supply (ตามหน้างานจริง) รวม/วัน (ถัง) = Σ  ( Supply (ตามหน้างานจริง) เฉลี่ย/วัน ของ SKU
                                             ÷ ขนาดถังของ SKU นั้น )

โดย:
  Supply (ตามหน้างานจริง) เฉลี่ย/วัน ของแต่ละ SKU
       = Supply (ตามหน้างานจริง) สะสมตั้งแต่ต้นเดือน ของ SKU นั้น
         ÷ จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา

  จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา
       = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่าง (โรงงานปทุมธานี, 3 SKU, วันที่ 15 ก.ค. 2026):</b><br>
                    สิงห์ 15L: Supply เฉลี่ย 30 L/วัน → 30÷15 = 2 ถัง<br>
                    สิงห์ 30L: Supply เฉลี่ย 100 L/วัน → 100÷30 = 3.33 ≈ 3 ถัง (ปัดลง)<br>
                    สิงห์ 50L: Supply เฉลี่ย 90 L/วัน → 90÷50 = 1.8 ≈ 2 ถัง (ปัดขึ้น)<br>
                    <b>ลบ:</b> ยอดเฉพาะ SKU 3 ตัวนี้ = <b>220 L (7-9 ถัง แล้วแต่ SKU)</b> — ตัวอย่างจำลอง<br>
                    <b>รวม (ลิตร)</b> = 30 + 100 + 90 = <b>220 L/วัน</b><br>
                    <b>รวม (ถัง)</b> = 2 + 3 + 2 = <b>7 ถัง/วัน</b> — ตัวเลขจริงในหน้าจอต้องรวมทั้ง 8 SKU ในคลัง = 9 ถัง</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • Supply (ตามหน้างานจริง) สะสมของแต่ละ SKU = ใบงาน "ผลิตเบียร์สด" (#02) ที่ปิดเสร็จ<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days"<br>
                    • ขนาดถังของแต่ละ SKU = ดึงจากตาราง Master_Product ช่อง NETVOLUME</div>
                  <div class="formula-important">⚠️ <b>ห้าม</b> คำนวณ "ถังรวม" โดยเอา "ลิตรรวม" หารด้วย 30 ก้อนใหญ่ — เบียร์แต่ละขนาดถังต่างกัน จะคลาดเคลื่อน 20-40% <b>ต้องแปลงทีละสินค้าก่อนแล้วค่อยรวม</b></div>
                </div>

              </div>
            </div>

            <!-- ============ Widget 5: Common Calculations ============ -->
            <div class="widget-group" id="widget-common">
              <div class="widget-group-title">⚙️ หลักการคำนวณกลาง (ใช้ทุกกล่อง)</div>
              <div class="widget-group-body">

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">1</span>จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา — สำหรับเดือนปัจจุบัน</div>
                  <div class="formula-location">📍 <b>ไม่แสดงตรง ๆ ในหน้าจอ</b> — เป็นค่ากลางที่ใช้คำนวณ Demand เฉลี่ย และ Supply เฉลี่ย</div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">Concept</div>
                    <div class="visual-mockup">
                      <div style="font-size:11px; line-height:1.7;">
                        📅 <b>วันนี้ 15/07/2026</b> — ก.ค. มี 31 วันปฏิทิน<br>
                        📅 ทีมกลยุทธ์กรอก: <b>จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = 26 วัน</b> (มีวันหยุด)<br>
                        ⏳ วันปฏิทินผ่านมา: <b>15 วัน</b><br>
                        ✅ ผลลัพธ์: 26 × (15/31) = 12.58 → <span class="visual-highlight">13 วัน</span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
                                    × (วันปฏิทินผ่านมา ÷ วันปฏิทินเต็มเดือน)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (วันที่ 15 ก.ค. 2026):</b><br>
                    Step 1: จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน ก.ค. = <b>26 วัน</b> (ทีมกลยุทธ์กรอก)<br>
                    Step 2: วันปฏิทินผ่านมา = <b>15 วัน</b> · วันปฏิทินเต็มเดือน ก.ค. = <b>31 วัน</b><br>
                    Step 3: จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา = 26 × (15÷31) = 12.58 ≈ <b>13 วัน</b> (ปัดขึ้น 0.58 ≥ 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มาของข้อมูล:</b><br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน = ทีมกลยุทธ์กรอกในหน้า "จัดการข้อมูล Stock Cover Days" (คอลัมน์ "จำนวนวันขาย")<br>
                    • วันปฏิทินผ่านมา = นับจากวันที่ 1 ถึงวันนี้<br>
                    • วันปฏิทินเต็มเดือน = จำนวนวันในเดือนนั้น (28/30/31)</div>
                  <div class="formula-important">⚠️ <b>ห้าม</b> หารด้วยวันปฏิทินตรง ๆ (เช่น หาร 31 หรือ 15) เพราะไม่สะท้อนวันขายจริง — บริษัทมีวันหยุด</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">2</span>การแปลงหน่วย ลิตร → ถัง (Per-SKU)</div>
                  <div class="formula-location">📍 <b>อยู่ที่:</b> ทุกจุดในหน้าจอที่ตัวเลขอยู่ในรูปแบบ <b>"X L (Y ถัง)"</b></div>
                  <div class="formula-visual">
                    <div class="formula-visual-label">ตัวอย่างจุดที่มีการแปลงหน่วย</div>
                    <div class="visual-mockup">
                      <div style="font-size:11px; line-height:1.8;">
                        Demand: <span class="visual-highlight">65 L (4 ถัง)</span>/วัน<br>
                        Stock ปัจจุบัน: <span class="visual-highlight">100 L (7 ถัง)</span><br>
                        ต้องผลิตเพิ่ม <span class="visual-highlight">1,205 L (80 ถัง)</span>
                      </div>
                    </div>
                  </div>
                  <div class="formula-code">ถัง = ลิตร ÷ ขนาดถังของสินค้า (Per-SKU)

ขนาดถังที่รองรับในระบบ: 15 / 20 / 22.7 / 22.9 / 25 / 30 / 50 ลิตร
(จากตาราง Master_Product ช่อง NETVOLUME — ทีมพัฒนา verify ก่อน implement)</div>
                  <div class="formula-example"><b>ตัวอย่างการคำนวณ (เบียร์สดสิงห์ 15L, ขนาดถัง 15 L):</b><br>
                    • 65 L (Demand เฉลี่ย/วัน) ÷ 15 = 4.33 ≈ <b>4 ถัง/วัน</b> (ปัดลง 0.33 &lt; 0.5)<br>
                    • 100 L (BG Stock ปัจจุบัน) ÷ 15 = 6.67 ≈ <b>7 ถัง</b> (ปัดขึ้น 0.67 ≥ 0.5)<br>
                    • 1,205 L (ต้องผลิตเพิ่ม) ÷ 15 = 80.33 ≈ <b>80 ถัง</b> (ปัดลง 0.33 &lt; 0.5)</div>
                  <div class="formula-source">🗂️ <b>ที่มา:</b> ขนาดถังของแต่ละ SKU ดึงจากตาราง Master_Product ช่อง NETVOLUME (ปริมาตรสุทธิ)<br>
                    <b>ถ้าไม่มีข้อมูลขนาดถังของสินค้าใด:</b> ระบบใช้ค่าเริ่มต้น 30 ลิตร (ตาม BRD)</div>
                  <div class="formula-important">⚠️ <b>ทำทีละสินค้าเสมอ</b> — ห้ามเอายอดรวมหลาย SKU หารด้วย 30 ก้อนใหญ่ (คลาดเคลื่อน 20-40% เพราะเบียร์แต่ละขนาดถังต่างกัน)</div>
                </div>

                <div class="formula-section">
                  <div class="formula-title"><span class="formula-number">3</span>การปัดเศษจำนวนถัง (round half up)</div>
                  <div class="formula-location">📍 <b>ใช้ทุกที่</b> ที่ต้องแปลง ลิตร → ถัง</div>
                  <div class="formula-code">ทศนิยม &lt; 0.5   →  ปัดลง
ทศนิยม ≥ 0.5   →  ปัดขึ้น</div>
                  <div class="formula-example"><b>ตัวอย่าง:</b><br>
                    • 3.33 ถัง → <b>3 ถัง</b>  (0.33 &lt; 0.5 → ปัดลง)<br>
                    • 6.67 ถัง → <b>7 ถัง</b>  (0.67 ≥ 0.5 → ปัดขึ้น)<br>
                    • 1.50 ถัง → <b>2 ถัง</b>  (0.50 ≥ 0.5 → ปัดขึ้น)<br>
                    • 13.10 ถัง → <b>13 ถัง</b>  (0.10 &lt; 0.5 → ปัดลง)<br>
                    • 12.58 วัน → <b>13 วัน</b>  (0.58 ≥ 0.5 → ปัดขึ้น) — ใช้กับ "จำนวนวันขายที่ผ่านมา" ด้วย</div>
                  <div class="formula-source">🗂️ <b>ใช้ที่ไหนบ้าง:</b><br>
                    • ทุกจุดที่แปลง ลิตร → ถัง (Alert Board, Bullet Chart, Heatmap)<br>
                    • จำนวนวันขาย (ตามเป้าหมาย) ที่ผ่านมา (Common หัวข้อ 1)<br>
                    • ตัวเลข SCVD ทุกจุด (เมื่อเป็นเลขทศนิยม)</div>
                  <div class="formula-important">⚠️ <b>ห้าม</b> ใช้ปัดขึ้นเสมอ หรือปัดลงเสมอ เพราะจะทำให้ตัวเลขเบี้ยว (เกิดผลกระทบสะสมกับ Demand/Supply รวมทั้งคลัง)</div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- ============ ⑥ EDIT-CRITERIA MODAL ============ -->
      <div v-if="showSetupModal" class="scvd-modal-overlay" @click.self="showSetupModal = false">
        <div class="scvd-modal">
          <div class="scvd-modal-header">
            <div>
              <h2>⚙️ แผงตั้งค่าเกณฑ์การแสดงผลจำนวน Stock Cover Days</h2>
              <div class="modal-subtitle">กำหนดเกณฑ์สถานะ (3-6 ระดับ) พร้อมสี แนวทางแก้ไข และ Target SCVD (จำลอง)</div>
            </div>
            <button class="scvd-modal-close" @click="showSetupModal = false">✕</button>
          </div>

          <div class="setup-modal-body">
            <div class="setup-info-bar">💡 เกณฑ์นี้กำหนดสีของสถานะสต็อกในทุกกราฟและตาราง — กำหนดได้ 3-6 ระดับ</div>
            <div class="setup-warning-bar">⚠️ "บันทึกตั้งเป็นค่าเริ่มต้น 🔒" มีผลกับผู้ใช้ทุกคนในบริษัท (เฉพาะแอดมิน Stock Cover Days เท่านั้น)</div>

            <div v-if="editLoading" class="text-center" style="padding:40px;">
              <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
            </div>

            <div v-else class="setup-section">
              <div class="setup-section-title">📊 กำหนดระดับสถานะและแนวทางแก้ไข</div>
              <table class="threshold">
                <thead>
                  <tr>
                    <th style="width:52px;">ลำดับ</th>
                    <th style="width:72px;">จากวัน</th>
                    <th style="width:72px;">ถึงวัน</th>
                    <th style="width:150px;">ชื่อสถานะ</th>
                    <th>แนวทางแก้ไข</th>
                    <th style="width:64px;">สี</th>
                    <th style="width:54px;">ลบ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(lv, i) in editLevels" :key="'edit-' + i">
                    <td>{{ i + 1 }}</td>
                    <td>
                      <input v-if="i === 0" type="number" min="0" step="1" v-model.number="lv.fromDay" readonly style="background:#f0f0f0;color:#999;">
                      <input v-else type="number" min="0" step="1" v-model.number="lv.fromDay" @keydown="blockIntKeys">
                    </td>
                    <td>
                      <input v-if="i === editLevels.length - 1" type="text" value="∞" readonly style="background:#f0f0f0;color:#999;">
                      <input v-else type="number" min="0" step="1" v-model.number="lv.toDay" @keydown="blockIntKeys">
                    </td>
                    <td><input type="text" maxlength="100" v-model="lv.name"></td>
                    <td><textarea maxlength="500" v-model="lv.actionText"></textarea></td>
                    <td><input type="color" class="color-picker" v-model="lv.colorHex"></td>
                    <td><button class="btn-tdelete" :disabled="editLevels.length <= 3" @click="deleteLevel(i)">🗑️</button></td>
                  </tr>
                </tbody>
              </table>
              <button class="btn-tadd" :disabled="editLevels.length >= 6" @click="addLevel">+ เพิ่มระดับใหม่</button>
              <div class="note-below">📌 "แนวทางแก้ไข" ใช้แสดงในรายงานทั้งหมด (Stock Cover Days Report + Dashboard: กล่อง "สินค้าที่ต้องดำเนินการเร่งด่วน")</div>
            </div>

            <div v-if="!editLoading" class="setup-section">
              <div class="setup-section-title">🎯 ระบุ Target SCVD (จำลอง)</div>
              <div class="target-row">
                <span>Target SCVD (จำลอง):</span>
                <input class="target-input" type="number" v-model.number="editTargetScvd">
                <span>วัน</span>
              </div>
            </div>
          </div>

          <div class="setup-footer">
            <button class="btn-sf btn-sf-primary" :disabled="editSaving" @click="saveOnce">บันทึกครั้งเดียว</button>
            <button v-if="adminCanSaveDefault" class="btn-sf btn-sf-warning" :disabled="editSaving" @click="saveDefault">
              บันทึกตั้งเป็นค่าเริ่มต้น <span class="badge-lock">🔒</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";
import select2 from "../../Shared/Select2";

require("sweetalert2");

export default {
  components: { select2 },
  data: function () {
    var self = this;
    var now = new Date();
    var currentYear = now.getFullYear();

    var authTransport = function (params, success, failure) {
      params.beforeSend = function (request) {
        request.setRequestHeader("Authorization", "Bearer " + self.$root.user.access_token);
      };
      var $request = $.ajax(params);
      $request.then(success);
      $request.fail(failure);
      return $request;
    };

    return {
      isLoading: false,
      bulletPlant: "", // QA-07b: "" = ทุกคลัง (ในขอบเขตตัวกรองด้านบน)
      bulletSort: "asc", // QA BUG-07: asc = วิกฤตสุด → ล้นสุด (ค่าเริ่มต้น) · desc = ล้นสุด → วิกฤตสุด
      dashboard: null,

      // ── ② pie in-pie drill-down (D1): level 1 สถานะ → 2 คลัง → 3 สินค้า (ภายใน pie เท่านั้น) ──
      pieDrill: { level: 1, statusOrder: null, plantCode: null },

      // ── filters ──────────────────────────────────────────────
      filterMonth: now.getMonth() + 1,
      filterYear: currentYear,
      filterPlantCodes: [],
      filterProductCodes: [],
      filterBrandIds: [],
      filterUnit: "ลิตร",

      monthOptions: [
        { value: 1, text: "January" }, { value: 2, text: "February" }, { value: 3, text: "March" },
        { value: 4, text: "April" }, { value: 5, text: "May" }, { value: 6, text: "June" },
        { value: 7, text: "July" }, { value: 8, text: "August" }, { value: 9, text: "September" },
        { value: 10, text: "October" }, { value: 11, text: "November" }, { value: 12, text: "December" },
      ],
      monthShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      yearOptions: [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4],

      // ── modals ───────────────────────────────────────────────
      showFormulaModal: false,
      showSetupModal: false,

      // ── ⑥ editor state ───────────────────────────────────────
      editLevels: [],
      editTargetScvd: 18,
      editLoading: false,
      editSaving: false,

      // ── session (บันทึกครั้งเดียว) override ────────────────────
      sessionCriteria: null,

      // ── Select2 options ──────────────────────────────────────
      plantOptions: {
        placeholder: "ทั้งหมด",
        allowClear: true,
        ajax: {
          url: "/Api/StockCoverDayTarget/Select2Plant",
          type: "post",
          delay: 300,
          dataType: "json",
          contentType: "application/json",
          data: function (params) {
            return JSON.stringify({ plantId: [], search: params.term || "", page: params.page || 1 });
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลคลัง"; } },
      },
      productOptions: {
        placeholder: "ทั้งหมด",
        allowClear: true,
        ajax: {
          url: "/Api/Product/Select2Product",
          delay: 300,
          data: function (params) {
            return { productGroupId: 14, productTypeId: 22, onlyStockCoverDays: true, onlyHasScvdMaster: true, search: params.term || "", page: params.page || 1 }; // CR-StockCoverDays A3+C3: เฉพาะ SKU ที่เปิดใช้งาน SCVD + มีข้อมูล Master
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลสินค้า"; } },
      },
      brandOptions: {
        placeholder: "ทั้งหมด",
        allowClear: true,
        ajax: {
          url: "/Api/ProductBrand/Select2ProductBrand",
          delay: 300,
          data: function (params) {
            return { search: params.term || "", page: params.page || 1 };
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลแบรนด์"; } },
      },
    };
  },

  computed: {
    criteria: function () {
      return this.dashboard ? this.dashboard.criteria : null;
    },
    criteriaLevels: function () {
      if (this.dashboard && this.dashboard.criteria && this.dashboard.criteria.levels) {
        return this.dashboard.criteria.levels;
      }
      return [];
    },
    // QA-07b: ตัวเลือกคลังของกล่องกราฟเทียบ = คลังใน Heatmap (ตามตัวกรองด้านบน · ไม่ถูกกรองด้วย dropdown ในกล่อง)
    bulletPlantOptions: function () {
      var hm = (this.dashboard && this.dashboard.heatmap) || [];
      return hm.map(function (p) { return { code: p.plantCode, name: p.plantName }; });
    },
    monthName: function () {
      var m = this.monthOptions.filter(function (o) { return o.value === Number(this.filterMonth); }, this)[0];
      return m ? m.text : "";
    },
    productChipText: function () {
      if (this.filterProductCodes.length) return this.filterProductCodes.length + " รายการ";
      if (this.dashboard && this.dashboard.pie) return "ทั้งหมด (" + this.dashboard.pie.totalSku + " SKU)";
      return "ทั้งหมด";
    },
    adminCanSaveDefault: function () {
      try {
        return this.$root.canShow([["SCVD_ADMIN", "COMPANY_PROFILE_TYPE_SCVD"]]);
      } catch (e) {
        return false;
      }
    },
    bulletByPlant: function () {
      var groups = [];
      var index = {};
      (this.dashboard && this.dashboard.bullet ? this.dashboard.bullet : []).forEach(function (b) {
        var key = b.plantCode || "-";
        if (index[key] === undefined) {
          index[key] = groups.length;
          groups.push({ plantCode: key, plantName: b.plantName, items: [] });
        }
        groups[index[key]].items.push(b);
      });
      return groups;
    },
    bulletScaleVal: function () {
      var levels = this.criteriaLevels;
      var scale = 40;
      if (levels.length) {
        var last = levels[levels.length - 1];
        var lastFrom = last.fromDay || 0;
        var prevSpan = levels.length > 1 ? (lastFrom - (levels[levels.length - 2].fromDay || 0)) : 10;
        if (prevSpan < 5) prevSpan = 10;
        scale = lastFrom + Math.max(prevSpan, 10);
      }
      var maxMarker = 0;
      (this.dashboard && this.dashboard.bullet ? this.dashboard.bullet : []).forEach(function (b) {
        ["actualScvd", "targetScvd", "targetSimScvd"].forEach(function (k) {
          if (b[k] != null && b[k] > maxMarker) maxMarker = b[k];
        });
      });
      if (maxMarker * 1.05 > scale) scale = Math.ceil(maxMarker * 1.05);
      return scale || 40;
    },
    bulletZones: function () {
      var scale = this.bulletScaleVal;
      var zones = [];
      this.criteriaLevels.forEach(function (lv) {
        var from = lv.fromDay || 0;
        var to = lv.toDay != null ? lv.toDay + 1 : scale;
        var left = (from / scale) * 100;
        var right = (to / scale) * 100;
        var w = Math.max(0, right - left);
        zones.push({
          name: lv.name,
          colorHex: lv.colorHex,
          widthPct: w,
          label: (lv.toDay != null ? lv.fromDay + "-" + lv.toDay : lv.fromDay + "+") + " วัน",
        });
      });
      return zones;
    },
    // D1: unified slice list ของ "ระดับ drill ปัจจุบัน" (level 1 สถานะ / 2 คลัง / 3 สินค้า)
    // — ทุกระดับ pre-loaded จาก payload เดียว (dashboard.pie.slices[].plants[].products[]).
    pieViewSlices: function () {
      var pie = this.dashboard && this.dashboard.pie ? this.dashboard.pie : null;
      if (!pie) return [];
      var d = this.pieDrill;
      var self = this;

      if (d.level === 1) {
        return (pie.slices || []).map(function (s) {
          return { code: s.order, colorHex: s.colorHex, percent: s.percent, count: s.count, name: s.name, drillable: s.count > 0 };
        });
      }

      var status = (pie.slices || []).filter(function (s) { return s.order === d.statusOrder; })[0];
      if (!status) return [];

      if (d.level === 2) {
        var plants = status.plants || [];
        return plants.map(function (p, i) {
          return { code: p.plantCode, colorHex: self.pieShade(status.colorHex, i, plants.length), percent: p.percent, count: p.count, name: p.plantName, drillable: p.count > 0 };
        });
      }

      // level 3 — สินค้าในคลัง (leaf: ไม่ drill ต่อ)
      var plant = (status.plants || []).filter(function (p) { return p.plantCode === d.plantCode; })[0];
      if (!plant) return [];
      var products = plant.products || [];
      return products.map(function (pr, i) {
        return { code: pr.productCode, colorHex: self.pieShade(status.colorHex, i, products.length), percent: pr.percent, count: pr.count, name: pr.productName, scvd: pr.scvd, drillable: false };
      });
    },
    // #66: segments carry both the donut arc (dash/offset) AND the leader-line + %/ชื่อ label
    // geometry — ported from mockup_dashboard (Spec v3) renderLevel2 math so labels stay dynamic.
    pieSegments: function () {
      var slices = this.pieViewSlices;
      var self = this;
      var cum = 0;
      var segs = [];
      slices.forEach(function (s, i) {
        var len = Number(s.percent) || 0;
        // midpoint บน stroke path: dashoffset=25 shift จุดเริ่ม arc ไปที่ stroke position 75
        var midStrokePos = (75 + cum + len / 2) % 100;
        var ang = (midStrokePos / 100) * 2 * Math.PI;   // มุมจากด้านบน (12 นาฬิกา) ตามเข็ม
        var sinA = Math.sin(ang), cosA = Math.cos(ang);
        var lx = 21 + 24 * sinA;
        var ly = 21 - 24 * cosA;
        var anchor = lx < 20 ? "end" : (lx > 22 ? "start" : "middle");
        var nameY = ly < 18 ? (ly - 2.6) : (ly + 2.8);   // ชื่ออยู่เหนือ % (ครึ่งบน) / ใต้ % (ครึ่งล่าง+กลาง)
        segs.push({
          index: i,
          colorHex: s.colorHex,
          drillable: s.drillable,
          dash: len + " " + (100 - len),
          offset: 25 - cum,
          showLabel: len > 0,
          pct: Math.round(len),
          name: self.pieShortName(s.name),
          labelColor: self.darken(s.colorHex, 0.35),
          lineX1: (21 + 21 * sinA).toFixed(2),
          lineY1: (21 - 21 * cosA).toFixed(2),
          lineX2: (21 + 23 * sinA).toFixed(2),
          lineY2: (21 - 23 * cosA).toFixed(2),
          lx: lx.toFixed(2),
          ly: ly.toFixed(2),
          nameY: nameY.toFixed(2),
          anchor: anchor,
        });
        cum += len;
      });
      return segs;
    },
    pieCenterCount: function () {
      return this.pieViewSlices.reduce(function (a, s) { return a + (Number(s.count) || 0); }, 0);
    },
    // D1: breadcrumb ทุกสถานะ › สถานะ › คลัง
    pieBreadcrumb: function () {
      var pie = this.dashboard && this.dashboard.pie ? this.dashboard.pie : null;
      var d = this.pieDrill;
      var crumbs = [{ label: "ทุกสถานะ", level: 1 }];
      if (!pie) return crumbs;
      if (d.level >= 2 && d.statusOrder != null) {
        var st = (pie.slices || []).filter(function (s) { return s.order === d.statusOrder; })[0];
        if (st) crumbs.push({ label: st.name, level: 2 });
      }
      if (d.level >= 3 && d.plantCode != null) {
        var st2 = (pie.slices || []).filter(function (s) { return s.order === d.statusOrder; })[0];
        var pl = st2 && st2.plants ? st2.plants.filter(function (p) { return p.plantCode === d.plantCode; })[0] : null;
        if (pl) crumbs.push({ label: pl.plantName, level: 3 });
      }
      return crumbs;
    },
    // D1 level 3: หัวตาราง + แถวรายสินค้า (เรียง SCVD น้อย→มาก) ตาม mockup renderLevel3
    pieDrillStatusSlice: function () {
      var pie = this.dashboard && this.dashboard.pie ? this.dashboard.pie : null;
      if (!pie) return null;
      var d = this.pieDrill;
      return (pie.slices || []).filter(function (s) { return s.order === d.statusOrder; })[0] || null;
    },
    pieDrillStatusName: function () { return this.pieDrillStatusSlice ? this.pieDrillStatusSlice.name : ""; },
    pieDrillStatusColor: function () { return this.pieDrillStatusSlice ? this.pieDrillStatusSlice.colorHex : "#374151"; },
    pieDrillPlantName: function () {
      var st = this.pieDrillStatusSlice;
      if (!st) return "";
      var pc = this.pieDrill.plantCode;
      var pl = (st.plants || []).filter(function (p) { return p.plantCode === pc; })[0];
      return pl ? pl.plantName : "";
    },
    pieLevel3Rows: function () {
      if (this.pieDrill.level !== 3) return [];
      // pieViewSlices ที่ level 3 = สินค้า (มี scvd) → เรียง SCVD น้อย→มาก (null ไปท้าย)
      return this.pieViewSlices.slice().sort(function (a, b) {
        var av = a.scvd == null ? Infinity : Number(a.scvd);
        var bv = b.scvd == null ? Infinity : Number(b.scvd);
        return av - bv;
      });
    },
    currentMonthIndex: function () {
      var hm = this.dashboard ? this.dashboard.heatmap : null;
      if (hm && hm.length && hm[0].rows.length) {
        var months = hm[0].rows[0].months;
        for (var i = 0; i < months.length; i++) {
          if (months[i].isCurrentMonth) return months[i].month - 1;
        }
      }
      return new Date().getMonth();
    },
  },

  mounted: function () {
    // D7: "บันทึกครั้งเดียว" = per-user/per-open-page เก็บใน memory เท่านั้น → reverts เมื่อ refresh (Dashboard.md §7.2)
    document.addEventListener("keydown", this.onKeydown);
    this.loadYears();       // #57: โหลดรายการปีแบบ dynamic (ตั้ง filterYear = latest)
    this.loadDashboard();   // #61: auto-load ทันทีเมื่อเข้าหน้า (ใช้ค่า filter เริ่มต้น)
  },

  // #61: auto-refresh เมื่อ filter ใด ๆ เปลี่ยน (debounce ~300ms กันยิง API ซ้ำถี่)
  watch: {
    filterMonth: function () { this.debouncedLoad(); },
    filterYear: function () { this.debouncedLoad(); },
    filterUnit: function () { this.debouncedLoad(); },
    filterPlantCodes: {
      handler: function (codes) {
        // QA-07b: คลังที่เลือกในกล่องกราฟเทียบหลุดจากตัวกรองด้านบน → กลับเป็น "ทั้งหมด"
        if (this.bulletPlant && codes && codes.length && codes.indexOf(this.bulletPlant) === -1) this.bulletPlant = "";
        this.debouncedLoad();
      },
      deep: true,
    },
    filterProductCodes: { handler: function () { this.debouncedLoad(); }, deep: true },
    filterBrandIds: { handler: function () { this.debouncedLoad(); }, deep: true },
  },

  beforeDestroy: function () {
    document.removeEventListener("keydown", this.onKeydown);
    if (this._loadTimer) clearTimeout(this._loadTimer);
  },

  methods: {
    // ── data load ────────────────────────────────────────────
    buildPayload: function () {
      return {
        month: Number(this.filterMonth),
        year: Number(this.filterYear),
        plantCodes: this.filterPlantCodes || [],
        productCodes: this.filterProductCodes || [],
        brandIds: (this.filterBrandIds || []).map(Number),
        unit: this.filterUnit,
        version: null,
        // P3 Simulation — session "what-if" config sent to server; applied to OPEN month only
        // (server ignores it for CLOSED months → frozen). null = no simulation.
        sessionLevels: this.sessionCriteria ? this.sessionCriteria.levels : null,
        sessionTargetScvd: this.sessionCriteria ? this.sessionCriteria.targetScvd : null,
        bulletSortDesc: this.bulletSort === "desc", // QA BUG-07
        bulletPlantCode: this.bulletPlant || null, // QA-07b
      };
    },

    // #57: โหลดรายการปีแบบ dynamic จาก backend — mirror ReportAnalysis.loadVersions (auth + { success, data } envelope)
    loadYears: function () {
      var self = this;
      axios
        .post(
          "/Api/StockCoverDayTarget/Dashboard/Years",
          {},
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function (response) {
          var years = [];
          var latest = null;
          // รองรับทั้ง envelope { success, data: { years, latest } } (แบบ loadVersions) และ body ดิบ { years, latest }
          var body = response.data || {};
          var payload = (body.data != null) ? body.data : body;
          years = payload.years || [];
          latest = payload.latest;
          if (years.length) {
            self.yearOptions = years;
            // default = latest · fallback: ปีปัจจุบัน (ถ้าอยู่ในรายการ) → ไม่งั้นปีแรก (มากสุด)
            var cy = new Date().getFullYear();
            if (latest != null && years.indexOf(latest) !== -1) self.filterYear = latest;
            else if (years.indexOf(cy) !== -1) self.filterYear = cy;
            else self.filterYear = years[0];
          }
          // ถ้า endpoint ไม่คืนปี → คง yearOptions static เดิมไว้ (fallback)
        })
        .catch(function (error) {
          // ไม่บล็อกหน้า — คงรายการปี static เดิม
          console.error("โหลดรายการปีไม่สำเร็จ:", error);
        });
    },

    // #61: debounce สำหรับ auto-refresh เมื่อ filter ใด ๆ เปลี่ยน (กันยิง API ซ้ำถี่)
    debouncedLoad: function () {
      var self = this;
      if (self._loadTimer) clearTimeout(self._loadTimer);
      self._loadTimer = setTimeout(function () {
        self._loadTimer = null;
        self.loadDashboard();
      }, 300);
    },

    loadDashboard: function () {
      var self = this;
      self.isLoading = true;
      axios
        .post("/Api/StockCoverDayTarget/Dashboard/Search", self.buildPayload(), {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function (response) {
          if (response.data && response.data.success && response.data.data) {
            var d = response.data.data;
            d.criteria = d.criteria || { targetScvd: 0, levels: [] };
            d.pie = d.pie || { totalSku: 0, noDataSku: 0, slices: [] };
            d.urgent = d.urgent || {
              tier1Count: 0, tier2Count: 0,
              tier1: { name: "", colorHex: "#ccc", actionText: "", cards: [], order: 1 },
              tier2: { name: "", colorHex: "#ccc", actionText: "", cards: [], order: 2 },
            };
            d.bullet = d.bullet || [];
            d.heatmap = d.heatmap || [];
            self.dashboard = d;
            self.pieDrill = { level: 1, statusOrder: null, plantCode: null };   // D1: กลับไป level 1 ทุกครั้งที่โหลดใหม่
            self.applySessionCriteriaOverride();
          } else {
            Swal.fire("เกิดข้อผิดพลาด", (response.data && response.data.message) || "ไม่สามารถโหลดข้อมูลได้", "error");
          }
        })
        .catch(function (error) {
          var message = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          Swal.fire("เกิดข้อผิดพลาด", message, "error");
        })
        .then(function () {
          self.isLoading = false;
        });
    },

    // ── filters ──────────────────────────────────────────────
    applyProductFilter: function (code, name) {
      // add option + select in the product Select2, then update model
      var $sel = $("#dbProductCodes");
      if ($sel.find("option[value='" + code + "']").length === 0) {
        $sel.append(new Option(name || code, code, true, true));
      }
      $sel.val([code]).trigger("change");
      this.filterProductCodes = [code];
    },
    cardClick: function (card) {
      this.applyProductFilter(card.productCode, card.productName);
      this.debouncedLoad();   // #61: reload (coalesced กับ filter watcher)
    },
    cellClick: function (row, cell) {
      this.filterMonth = cell.month;
      this.applyProductFilter(row.productCode, row.productName);
      this.debouncedLoad();   // #61: reload (coalesced กับ filter watcher)
    },

    // ── ② D1 pie drill-down (ภายใน pie เท่านั้น — ไม่กระทบ widget อื่น, Dashboard.md §4.2) ──
    drillInto: function (i) {
      var s = this.pieViewSlices[i];
      if (!s || !s.drillable) return;
      var d = this.pieDrill;
      if (d.level === 1) {
        this.pieDrill = { level: 2, statusOrder: s.code, plantCode: null };   // สถานะ → คลัง
      } else if (d.level === 2) {
        this.pieDrill = { level: 3, statusOrder: d.statusOrder, plantCode: s.code };   // คลัง → สินค้า
      }
      // level 3 = leaf (สินค้า) → ไม่ drill ต่อ
    },
    drillUp: function () {
      var d = this.pieDrill;
      if (d.level === 3) this.pieDrill = { level: 2, statusOrder: d.statusOrder, plantCode: null };
      else if (d.level === 2) this.pieDrill = { level: 1, statusOrder: null, plantCode: null };
    },
    drillToLevel: function (level) {
      var d = this.pieDrill;
      if (level === 1) this.pieDrill = { level: 1, statusOrder: null, plantCode: null };
      else if (level === 2) this.pieDrill = { level: 2, statusOrder: d.statusOrder, plantCode: null };
      // คลิก crumb ระดับปัจจุบัน (level 3) = คงเดิม
    },
    // แสดง SCVD ในตาราง L3: null → "-" · ทศนิยม 1 ตำแหน่ง (ตรง mockup s.scvd.toFixed(1))
    pieScvdText: function (v) { return (v == null) ? "-" : Number(v).toFixed(1); },
    // shade สีของสถานะ (tier) แยกแต่ละ คลัง/สินค้า ให้ยังผูกกับสีสถานะ (§ color rule)
    pieShade: function (baseHex, i, n) {
      if (!n || n <= 1) return baseHex;
      var amt = 0.1 + (0.6 * i) / (n - 1);   // ไล่เฉด 0.1 → 0.7 (อ่อนลง) ให้ชิ้นติดกันแยกออก
      return this.tint(baseHex, amt);
    },
    // #66: ย่อชื่อคลังสำหรับ label รอบโดนัท (ตัด prefix ให้ fit viewBox — ตรง mockup renderLevel2 shortName)
    pieShortName: function (name) {
      if (!name) return "";
      return String(name).replace(/^โรงงาน/, "").replace(/^ศูนย์กระจายสินค้า/, "");
    },
    removeFilter: function (which) {
      if (which === "plant") {
        this.filterPlantCodes = [];
        $("#dbPlantCodes").val(null).trigger("change");
      } else if (which === "product") {
        this.filterProductCodes = [];
        $("#dbProductCodes").val(null).trigger("change");
      } else if (which === "brand") {
        this.filterBrandIds = [];
        $("#dbBrandId").val(null).trigger("change");
      }
      this.debouncedLoad();   // #61: reload (coalesced กับ filter watcher)
    },
    clearFilters: function () {
      var now = new Date();
      this.filterMonth = now.getMonth() + 1;
      this.filterYear = now.getFullYear();
      this.filterPlantCodes = [];
      this.filterProductCodes = [];
      this.filterBrandIds = [];
      this.bulletPlant = "";
      $("#dbPlantCodes").val(null).trigger("change");
      $("#dbProductCodes").val(null).trigger("change");
      $("#dbBrandId").val(null).trigger("change");
      this.debouncedLoad();   // #61: reload (coalesced กับ filter watcher)
    },

    // ── ⑤ / ⑥ modals ─────────────────────────────────────────
    openFormula: function () {
      this.showFormulaModal = true;
    },
    scrollToWidget: function (id) {
      // R2-20: scroll to the section INSIDE the formula modal (native href="#id" made the SPA redirect to /#id)
      var el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    openSetup: function () {
      this.showSetupModal = true;
      this.loadThreshold();
    },
    onKeydown: function (e) {
      if (e.key === "Escape") {
        this.showFormulaModal = false;
        this.showSetupModal = false;
      }
    },
    loadThreshold: function () {
      var self = this;
      self.editLoading = true;
      axios
        .get("/Api/StockCoverDayTarget/Threshold", {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function (response) {
          var d = response.data && response.data.data ? response.data.data : null;
          if (d) {
            self.editTargetScvd = d.targetScvd;
            self.editLevels = (d.levels || []).map(function (lv) {
              return {
                order: lv.order,
                fromDay: lv.fromDay,
                toDay: lv.toDay,
                name: lv.name,
                colorHex: lv.colorHex,
                actionText: lv.actionText,
              };
            });
            if (self.editLevels.length) self.editLevels[0].fromDay = 0;   // #62: ระดับแรก "จากวัน" ล็อค = 0 เสมอ
          }
        })
        .catch(function () {
          Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถโหลดเกณฑ์ได้", "error");
        })
        .then(function () {
          self.editLoading = false;
        });
    },
    addLevel: function () {
      if (this.editLevels.length >= 6) return;
      var last = this.editLevels[this.editLevels.length - 1];
      var newFrom = 0;
      if (last) {
        // ปิดช่วงของระดับสุดท้ายเดิม (∞) ให้เป็นตัวเลข เพื่อให้ต่อเนื่อง
        if (last.toDay == null) last.toDay = (last.fromDay || 0) + 3;
        newFrom = (last.toDay || 0) + 1;
      }
      this.editLevels.push({
        order: this.editLevels.length + 1,
        fromDay: newFrom,
        toDay: null,
        name: "",
        colorHex: "#cccccc",
        actionText: "",
      });
    },
    deleteLevel: function (i) {
      if (this.editLevels.length <= 3) return;
      this.editLevels.splice(i, 1);
      // renumber + ensure last = ∞
      for (var j = 0; j < this.editLevels.length; j++) this.editLevels[j].order = j + 1;
      this.editLevels[this.editLevels.length - 1].toDay = null;
      this.editLevels[0].fromDay = 0;   // #62: ระดับแรก "จากวัน" ล็อค = 0 เสมอ
    },
    // #62: บล็อกอักขระที่ทำให้ไม่ใช่จำนวนเต็มไม่ติดลบ (. - + e E) ในช่อง "จากวัน"/"ถึงวัน"
    blockIntKeys: function (e) {
      if ([".", "-", "+", "e", "E"].indexOf(e.key) !== -1) e.preventDefault();
    },
    normalizedLevels: function () {
      var out = [];
      for (var i = 0; i < this.editLevels.length; i++) {
        var lv = this.editLevels[i];
        out.push({
          order: i + 1,
          fromDay: i === 0 ? 0 : (Number(lv.fromDay) || 0),
          toDay: i === this.editLevels.length - 1 ? null : (lv.toDay == null ? null : Number(lv.toDay)),
          name: lv.name,
          colorHex: lv.colorHex,
          actionText: lv.actionText,
        });
      }
      return out;
    },
    saveOnce: function () {
      // D7 "บันทึกครั้งเดียว": in-memory เท่านั้น (per-user / per-open-page) — reverts เมื่อ refresh (ไม่เรียก API, ไม่กระทบผู้ใช้อื่น)
      var sc = { targetScvd: Number(this.editTargetScvd) || 0, levels: this.normalizedLevels() };
      this.sessionCriteria = sc;
      this.applySessionCriteriaOverride();   // instant client feedback (bullet/criteria)
      // P3: re-query so the server re-tiers the OPEN month (pie/urgent/heatmap) with the session config.
      // CLOSED months stay frozen (server ignores sessionLevels for them).
      this.loadDashboard();
      this.showSetupModal = false;
      Swal.fire({
        icon: "success",
        title: "บันทึกครั้งเดียว",
        text: "ใช้เกณฑ์นี้เฉพาะการดูของคุณในหน้านี้ (ไม่กระทบผู้ใช้อื่น/เดือนที่ปิดรอบแล้ว) — ค่าจะกลับเป็นค่าเริ่มต้นเมื่อรีเฟรชหน้า",
      });
    },
    saveDefault: function () {
      var self = this;
      Swal.fire({
        icon: "warning",
        title: "บันทึกเป็นค่าเริ่มต้น (ทั้งบริษัท)",
        html: "เกณฑ์นี้จะมีผลกับ <b>ผู้ใช้ทุกคนในบริษัท</b> และจะแทนที่ค่าเดิมทั้งหมด<br>ยืนยันการบันทึกหรือไม่?",
        showCancelButton: true,
        confirmButtonText: "ยืนยันบันทึก",
        cancelButtonText: "ยกเลิก",
      }).then(function (res) {
        if (!res.value) return;
        self.editSaving = true;
        axios
          .post(
            "/Api/StockCoverDayTarget/Threshold",
            { targetScvd: Number(self.editTargetScvd) || 0, levels: self.normalizedLevels() },
            { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
          )
          .then(function (response) {
            if (response.data && response.data.success) {
              // ค่าเริ่มต้นใหม่ = server → เคลียร์ session override (in-memory)
              self.sessionCriteria = null;
              self.showSetupModal = false;
              Swal.fire("สำเร็จ", (response.data.message) || "บันทึกเกณฑ์เป็นค่าเริ่มต้นสำเร็จ", "success");
              self.loadDashboard();
            } else {
              Swal.fire("ไม่สำเร็จ", (response.data && response.data.message) || "บันทึกไม่สำเร็จ", "error");
            }
          })
          .catch(function (error) {
            var message = "ไม่สามารถบันทึกได้";
            if (error.response && error.response.status === 403) message = "คุณไม่มีสิทธิ์บันทึกเป็นค่าเริ่มต้น (เฉพาะแอดมิน Stock Cover Days)";
            else if (error.response && error.response.data && error.response.data.message) message = error.response.data.message;
            Swal.fire("เกิดข้อผิดพลาด", message, "error");
          })
          .then(function () {
            self.editSaving = false;
          });
      });
    },
    applySessionCriteriaOverride: function () {
      if (!this.sessionCriteria || !this.dashboard) return;
      // P4: a CLOSED month is frozen — never let a lingering session config override its display.
      if (this.dashboard.isClosed) return;
      var sc = this.sessionCriteria;
      var c = this.dashboard.criteria || {};
      c.targetScvd = sc.targetScvd;
      c.levels = sc.levels;
      this.dashboard.criteria = c;
      // bullet purple marker (targetSim) = session target
      (this.dashboard.bullet || []).forEach(function (b) {
        b.targetSimScvd = sc.targetScvd;
      });
    },

    // ── display helpers ──────────────────────────────────────
    badgeLabel: function (lv) {
      return lv.toDay != null ? lv.fromDay + "-" + lv.toDay + " " + lv.name : lv.fromDay + "+ " + lv.name;
    },
    rangeForOrder: function (order) {
      var lv = this.criteriaLevels.filter(function (x) { return x.order === order; })[0];
      if (!lv) return "";
      return (lv.toDay != null ? lv.fromDay + "–" + lv.toDay : lv.fromDay + "+") + " วัน";
    },
    tierName: function (scvd) {
      if (scvd == null) return "ไม่มีข้อมูล";
      var v = Math.round(scvd);
      var lv = this.criteriaLevels.filter(function (x) {
        return v >= x.fromDay && (x.toDay == null || v <= x.toDay);
      })[0];
      return lv ? lv.name : "-";
    },
    fmt: function (v) {
      if (v == null) return "0";
      return Math.round(v).toLocaleString("en-US");
    },
    scvdDisplay: function (v) {
      if (v == null) return "-";
      return String(Math.round(v));
    },
    formatPct: function (v) {
      if (v == null) return "0";
      return (Number(v).toFixed(1)).replace(/\.0$/, "");
    },
    stockText: function (item) {
      // BG Stock (ตามหน้างานจริง) ปัจจุบัน — ใช้ค่าจริงจาก backend (mirror bullet formula 1) ถ้ามี
      if (item.actualBgCurrentLitre != null) {
        var nv = item.netVolume != null ? Number(item.netVolume) : 0;
        var keg = nv > 0 ? Math.round(item.actualBgCurrentLitre / nv) : Math.round(item.actualBgCurrentLitre);
        return this.fmt(item.actualBgCurrentLitre) + " L (" + this.fmt(keg) + " ถัง)";
      }
      if (item.actualScvd == null) return "";
      var l = item.actualScvd * item.demandPerDayLitre;
      var k = item.actualScvd * item.demandPerDayKeg;
      return this.fmt(l) + " L (" + this.fmt(k) + " ถัง)";
    },
    trendInfo: function (card) {
      var t = card.trend;
      var d = card.scvdDelta;
      var abs = d != null ? Math.abs(Math.round(d)) : null;
      if (t === "down") return { text: "▼ " + (abs != null ? abs + " วัน " : "") + "จากเดือนก่อน", cls: "down" };
      if (t === "up") return { text: "▲ " + (abs != null ? abs + " วัน " : "") + "(ดีขึ้น)", cls: "up" };
      if (t === "flat") return { text: "━ ไม่เปลี่ยน", cls: "stable" };
      return { text: "— ไม่มีข้อมูลเทียบ", cls: "stable" };
    },
    bulletPos: function (val) {
      if (val == null) return null;
      var p = (val / this.bulletScaleVal) * 100;
      if (p < 0) p = 0;
      if (p > 100) p = 100;
      return p;
    },
    // ── ③ ส่วนต่างการวิเคราะห์ 3 รูปแบบ (Dashboard.md §③ · BRD §4.4.6) ──────────────
    // % วัดจาก Supply (action number) ไม่ใช่ Demand · แปลง ลิตร→ถัง per-SKU (NETVOLUME).
    // Raw inputs (ลิตร) มาจาก backend (mirror widget data) → คำนวณฝั่ง client เพื่อ recompute
    // สดเมื่อผู้ใช้ override Target SCVD (จำลอง) แบบ "บันทึกครั้งเดียว".
    gapRows: function (item) {
      var self = this;
      var nv = item.netVolume != null ? Number(item.netVolume) : 0;
      var qtyText = function (gapLitre) {
        var abs = Math.abs(gapLitre);
        var keg = nv > 0 ? Math.round(abs / nv) : Math.round(abs);
        return self.fmt(abs) + " L (" + self.fmt(keg) + " ถัง)";
      };
      // gap > 0 = ต่ำกว่าเป้า (ต้องผลิตเพิ่ม, แดง) · gap < 0 = สูงกว่าเป้า (น้ำเงิน) · 0 = ตรงเป้า (เขียว)
      var build = function (gapLitre, denomLitre, mode) {
        if (gapLitre == null || denomLitre == null || denomLitre === 0) {
          return { text: "ไม่มีข้อมูลเพียงพอสำหรับเปรียบเทียบ", cls: "neutral" };
        }
        if (Math.round(Math.abs(gapLitre)) === 0) {
          return { text: "✅ ตรงตามเป้าหมาย — ไม่ต้องปรับ", cls: "ok" };
        }
        var pct = (Math.abs(gapLitre) / Math.abs(denomLitre)) * 100;
        var pctStr = pct.toFixed(1).replace(/\.0$/, "");
        var qty = qtyText(gapLitre);
        if (gapLitre > 0) {
          var verb = mode === 3 ? "ต้องปรับเพิ่มแผนผลิต" : "ต้องผลิตเพิ่ม";
          return { text: "🔻 ห่างจากเป้าหมาย " + pctStr + "% — " + verb + " " + qty, cls: "critical" };
        }
        var verb2 = mode === 3 ? "ต้องปรับลดแผนผลิต" : "ต้องระบายสต๊อก";
        return { text: "▲ สูงกว่าเป้าหมาย " + pctStr + "% — " + verb2 + " " + qty, cls: "planning" };
      };

      // raw inputs (ลิตร) จาก backend
      var tSupply = item.targetSupplyFullMonthLitre;   // Supply (ตามเป้าหมาย) เต็มเดือน
      var tDemand = item.targetDemandFullMonthLitre;   // Demand (ตามเป้าหมาย) เต็มเดือน
      var tBgStart = item.targetBgStartLitre;          // BG Stock ต้นเดือน (ตามเป้าหมาย)
      var tSaleDays = item.targetSaleDaysFullMonth;    // จำนวนวันขาย (ตามเป้าหมาย) ทั้งเดือน
      var aSupplyCum = item.actualSupplyCumLitre;      // Supply (ตามหน้างานจริง) สะสม
      var aBgCurrent = item.actualBgCurrentLitre;      // BG Stock (ตามหน้างานจริง) ปัจจุบัน
      var tsim = item.targetSimScvd;                   // Target SCVD (จำลอง) — live session override

      // รูปแบบ 1: ส่วนต่าง Supply = Supply(เป้าหมาย)เต็มเดือน − Supply(จริง)สะสม ; % ÷ Supply(เป้าหมาย)
      var gap1 = null, denom1 = null;
      if (tSupply != null && aSupplyCum != null) { gap1 = tSupply - aSupplyCum; denom1 = tSupply; }

      // BG Stock ที่ต้องมี = Target SCVD × Demand(เป้าหมาย)เต็มเดือน ÷ วันขาย(เป้าหมาย)ทั้งเดือน (รูปแบบ 2/3)
      var bgNeeded = null;
      if (tsim != null && tDemand != null && tSaleDays != null && tSaleDays > 0) {
        bgNeeded = tsim * tDemand / tSaleDays;
      }
      // รูปแบบ 2: Supply ต้องเร่ง = BG ที่ต้องมี − BG(จริง)ปัจจุบัน ; % ÷ BG ที่ต้องมี
      var gap2 = null, denom2 = null;
      if (bgNeeded != null && aBgCurrent != null) { gap2 = bgNeeded - aBgCurrent; denom2 = bgNeeded; }
      // รูปแบบ 3: Supply ต้องปรับ = BG ที่ต้องมี − BG ต้นเดือน(เป้าหมาย) ; % ÷ BG ที่ต้องมี
      var gap3 = null, denom3 = null;
      if (bgNeeded != null && tBgStart != null) { gap3 = bgNeeded - tBgStart; denom3 = bgNeeded; }

      return [
        { num: 1, fromCls: "mk-actual", fromText: "ข้อมูลตามหน้างานจริง", toCls: "mk-target-master", toText: "ข้อมูลตามเป้าหมาย", gap: build(gap1, denom1, 1) },
        { num: 2, fromCls: "mk-actual", fromText: "ข้อมูลตามหน้างานจริง", toCls: "mk-target-scvd", toText: "Target SCVD (จำลอง)", gap: build(gap2, denom2, 2) },
        { num: 3, fromCls: "mk-target-master", fromText: "ข้อมูลตามเป้าหมาย", toCls: "mk-target-scvd", toText: "Target SCVD (จำลอง)", gap: build(gap3, denom3, 3) },
      ];
    },
    cellStyle: function (cell) {
      if (cell.scvd == null || !cell.colorHex) {
        return { background: "#f3f4f6", color: "#9ca3af" };
      }
      return { background: cell.colorHex, color: this.textColor(cell.colorHex) };
    },

    // ── color helpers ────────────────────────────────────────
    parseHex: function (hex) {
      hex = (hex || "#cccccc").replace("#", "");
      if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
      };
    },
    toHex: function (r, g, b) {
      var f = function (n) {
        var s = Math.max(0, Math.min(255, Math.round(n))).toString(16);
        return s.length === 1 ? "0" + s : s;
      };
      return "#" + f(r) + f(g) + f(b);
    },
    mix: function (hex, targetHex, amount) {
      var a = this.parseHex(hex), b = this.parseHex(targetHex);
      return this.toHex(a.r + (b.r - a.r) * amount, a.g + (b.g - a.g) * amount, a.b + (b.b - a.b) * amount);
    },
    tint: function (hex, amount) {
      return this.mix(hex, "#ffffff", amount);
    },
    darken: function (hex, amount) {
      return this.mix(hex, "#000000", amount);
    },
    textColor: function (hex) {
      var c = this.parseHex(hex);
      var lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
      return lum > 0.62 ? "#1a1a1a" : "#ffffff";
    },
  },
};
</script>

<style scoped>
.scvd-dashboard { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Sarabun", "Tahoma", Arial, sans-serif; color: #1a1a1a; font-size: 14px; }
.scvd-container { background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 0 20px rgba(0,0,0,0.05); }

/* Header */
.scvd-header { background: linear-gradient(135deg, #FFDB00 0%, #f4c800 100%); color: #58595B; padding: 14px 24px; display: flex; justify-content: space-between; align-items: center; }
.scvd-header h1 { font-size: 22px; font-weight: 700; margin: 0; }
.scvd-header .subtitle { font-size: 12.5px; opacity: 0.8; margin-top: 2px; }
.scvd-header .meta { font-size: 11px; color: rgba(0,0,0,0.7); text-align: right; display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
.scvd-header .sync-status { display: inline-flex; align-items: center; gap: 5px; background: rgba(0,0,0,0.06); padding: 2px 8px; border-radius: 10px; font-size: 10px; }
.scvd-header .sync-dot { width: 6px; height: 6px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 4px #22c55e; }

/* Criteria bar */
.setup-collapsed { background: #fffdf0; padding: 10px 24px; border-bottom: 1px solid #f0e0a0; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.criteria-summary { display: flex; align-items: center; gap: 8px; font-size: 12px; flex-wrap: wrap; }
.criteria-badge { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; }
.target-badge { padding: 3px 10px; border-radius: 12px; background: #f3e8ff; color: #7e22ce; font-weight: 600; font-size: 11px; }
.btn-edit { padding: 6px 14px; background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 11px; cursor: pointer; color: #64748b; font-weight: 500; white-space: nowrap; }
.btn-edit:hover { background: #f8fafc; }

/* Filter bar */
.filter-bar { padding: 16px 24px; background: #fff; border-bottom: 1px solid #e5e7eb; display: flex; gap: 14px; align-items: flex-end; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 4px; min-width: 120px; }
.filter-group label { font-size: 11px; color: #6b7280; font-weight: 500; }
.scvd-select { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; font-size: 13px; color: #1a1a1a; cursor: pointer; height: 36px; }
.btn-primary-apply { height: 36px; padding: 0 16px; background: #58595B; color: #fff; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; }
.btn-primary-apply:hover { background: #3d3e40; }
.btn-formula { height: 36px; margin-left: auto; padding: 0 14px; background: rgba(255,255,255,0.85); border: 1px solid rgba(0,0,0,0.15); border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: 600; color: #1a1a1a; }
.btn-formula:hover { background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.1); }

/* Active filter strip */
.active-filter-strip { padding: 10px 24px; background: #eef2ff; border-bottom: 1px solid #c7d2fe; display: flex; align-items: center; gap: 10px; font-size: 12px; color: #4338ca; flex-wrap: wrap; }
.filter-strip-label { font-weight: 600; }
.filter-chip { padding: 4px 6px 4px 12px; background: #fff; border: 1px solid #c7d2fe; border-radius: 16px; display: inline-flex; align-items: center; gap: 6px; font-weight: 500; color: #1a1a1a; }
.filter-chip .remove-chip { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: #f3f4f6; border-radius: 50%; color: #6b7280; font-weight: bold; cursor: pointer; font-size: 12px; }
.filter-chip .remove-chip:hover { background: #e5e7eb; }
.btn-clear-filter { margin-left: auto; padding: 4px 10px; background: transparent; border: 1px solid #a5b4fc; border-radius: 4px; font-size: 11px; color: #4338ca; cursor: pointer; }

/* Loading / empty */
.scvd-loading { text-align: center; padding: 60px 20px; color: #6b7280; }
.scvd-empty-block { text-align: center; padding: 30px 20px; color: #94a3b8; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px; }

/* Dashboard grid */
.dashboard { padding: 20px 24px 36px 24px; background: #f5f7fa; display: flex; flex-direction: column; gap: 20px; }
.row-2col { display: grid; grid-template-columns: 3fr 2fr; gap: 20px; }
@media (max-width: 1100px) { .row-2col { grid-template-columns: 1fr; } }

/* Widget base */
.widget { background: #fff; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); padding: 20px; border: 1px solid #f0f0f0; }
.widget-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; gap: 12px; }
.widget-title { font-size: 15px; font-weight: 700; color: #1a1a1a; }
.widget-hint { font-size: 11px; color: #6b7280; font-style: italic; }
.bullet-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; justify-content: flex-end; flex-shrink: 0; }
.bullet-sort { width: auto; min-width: 0; }
.bullet-header-wrap .widget-title { flex: 1 1 auto; min-width: 0; } /* QA-07b: ให้ชื่อกล่องตัดบรรทัด แทน dropdown ตกบรรทัด */
@media (max-width: 1200px) { .bullet-toolbar { flex-wrap: wrap; } .bullet-toolbar .widget-hint { display: none; } }

/* ② Alert summary */
.alert-summary { display: flex; gap: 12px; margin-bottom: 14px; }
.alert-summary-item { flex: 1; padding: 10px 14px; border-radius: 8px; display: flex; flex-direction: column; gap: 8px; }
.alert-summary-top { display: flex; justify-content: space-between; align-items: center; }
.alert-summary-count-row { display: flex; align-items: baseline; gap: 6px; }
.alert-summary-count { font-size: 24px; font-weight: 800; line-height: 1; color: #1a1a1a; }
.alert-summary-unit { font-size: 13px; font-weight: 700; color: #1a1a1a; }
.alert-summary-desc { font-size: 10px; color: #6b7280; margin-top: 3px; }
.alert-summary-label { font-size: 11px; color: #4b5563; font-weight: 500; text-align: right; }
.alert-summary-guidance { font-size: 11.5px; font-weight: 600; padding: 7px 10px; background: rgba(255,255,255,0.7); border-radius: 5px; line-height: 1.4; }

/* ② Alert grid + cards */
.alert-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; align-items: start; }
.alert-column { display: flex; flex-direction: column; gap: 10px; max-height: 640px; overflow-y: auto; }
.alert-empty-state { padding: 40px 20px; text-align: center; color: #94a3b8; font-size: 13px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px; min-height: 120px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 8px; }
.alert-empty-state .empty-emoji { font-size: 24px; }
.alert-card { background: #fff; border: 1px solid #e5e7eb; border-left: 4px solid #ccc; border-radius: 8px; padding: 6px 12px; cursor: pointer; transition: all 0.15s; }
.alert-card:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.1); border-color: #d1d5db; }
.alert-card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.alert-status { font-size: 9.5px; font-weight: 700; padding: 2px 8px; border-radius: 4px; }
.alert-trend { font-size: 10.5px; font-weight: 600; display: flex; align-items: center; gap: 2px; }
.alert-trend.down { color: #b04747; }
.alert-trend.up { color: #2c6b3d; }
.alert-trend.stable { color: #6b7280; }
.alert-sku { font-size: 14px; font-weight: 700; color: #1a1a1a; margin-bottom: 1px; }
.alert-warehouse { font-size: 11px; color: #6b7280; margin-bottom: 2px; }
.alert-metric-row { display: flex; justify-content: space-between; align-items: flex-end; padding-top: 3px; border-top: 1px dashed #f0f0f0; }
.alert-metric-left { display: flex; align-items: baseline; gap: 4px; flex-wrap: wrap; }
.alert-value { font-size: 28px; font-weight: 800; line-height: 1; }
.alert-unit { font-size: 12px; color: #6b7280; }
.alert-metric-label { font-size: 9.5px; color: #9ca3af; font-style: italic; width: 100%; display: block; }
.alert-target { font-size: 10.5px; color: #6b7280; text-align: right; line-height: 1.35; }
.alert-target-src { font-size: 9px; color: #9ca3af; }

/* ② Pie */
.pie-container { display: flex; flex-direction: column; align-items: stretch; gap: 14px; }
.pie-svg { width: 100%; max-width: 340px; height: auto; margin: 0 auto; display: block; }
/* #66: hover = ชิ้นที่ชี้เด้ง/นูนออก (stroke หนาขึ้น + สว่างขึ้น + label ใหญ่ขึ้น) — ตรง mockup Spec v3 */
.pie-slice-clickable { transition: all 0.15s; }
.pie-slice-clickable:hover circle { stroke-width: 12; filter: brightness(1.08); }
.pie-slice-clickable:hover text { font-size: 3.6; }
.pie-legend { display: flex; flex-direction: row; flex-wrap: wrap; justify-content: center; gap: 8px 16px; padding: 12px 14px; background: #f9fafb; border-radius: 6px; font-size: 11.5px; border: 1px solid #f0f0f0; }
.pie-legend-item { display: flex; align-items: center; gap: 6px; padding: 3px 6px; border-radius: 4px; white-space: nowrap; }
.pie-color-box { width: 12px; height: 12px; border-radius: 3px; flex-shrink: 0; }
.pie-legend-item strong { color: #1a1a1a; font-weight: 700; min-width: 30px; }
.pie-legend-item-desc { color: #6b7280; font-size: 11px; }
.pie-legend-item.clickable { cursor: pointer; }
.pie-legend-item.clickable:hover { background: #eef2ff; }
.pie-legend-drill { color: #4338ca; font-weight: 700; }
.pie-nodata-note { font-size: 10.5px; color: #9ca3af; text-align: center; font-style: italic; }

/* ② D1 pie drill-down bar / breadcrumb */
.pie-drill-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
.pie-crumbs { display: flex; align-items: center; gap: 4px; font-size: 11.5px; flex-wrap: wrap; }
.pie-crumb { color: #4338ca; cursor: pointer; padding: 2px 8px; border-radius: 12px; background: #eef2ff; border: 1px solid #c7d2fe; font-weight: 600; }
.pie-crumb:hover { background: #e0e7ff; }
.pie-crumb.active { color: #1a1a1a; background: #FFDB00; border-color: #f4c800; cursor: default; }
.pie-crumb-sep { color: #94a3b8; font-weight: 700; }
.pie-back-btn { padding: 3px 10px; background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 11px; color: #475569; cursor: pointer; font-weight: 600; white-space: nowrap; }
.pie-back-btn:hover { background: #f1f5f9; }

/* ③ Bullet */
.bullet-warehouse { margin-bottom: 20px; }
.bullet-warehouse:last-child { margin-bottom: 0; }
.bullet-warehouse-title { font-size: 13.5px; font-weight: 700; color: #1a1a1a; margin-bottom: 6px; padding-bottom: 6px; border-bottom: 2px solid #FFDB00; }
.bullet-row { padding: 14px 12px 12px 12px; border-bottom: 1px solid #f0f0f0; border-radius: 6px; cursor: pointer; }
.bullet-row:hover { background: #fafbfc; }
.bullet-row:last-child { border-bottom: none; }
.bullet-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; gap: 12px; }
.bullet-sku-info { display: flex; align-items: center; gap: 10px; }
.bullet-sku { font-size: 14px; font-weight: 700; color: #1a1a1a; }
.bullet-badge { padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; }
.bullet-demand { font-size: 11px; color: #6b7280; text-align: right; line-height: 1.4; }
.bullet-chart-wrapper { padding: 104px 0 24px 0; position: relative; }
.bullet-chart { position: relative; height: 28px; display: flex; border-radius: 4px; }
.bullet-zone { height: 100%; display: flex; align-items: center; justify-content: center; font-size: 9px; color: rgba(0,0,0,0.55); font-weight: 600; overflow: hidden; }
.bullet-zone:first-child { border-radius: 4px 0 0 4px; }
.bullet-zone:last-child { border-radius: 0 4px 4px 0; }
/* #69: ACTUAL (ข้อมูลตามหน้างานจริง) bar = FIXED teal #0891B2 (matches mockup .bullet-actual + .mk-actual), NOT the tier/status color */
.bullet-actual { position: absolute; top: 6px; left: 0; height: 16px; background: #0891B2; border-radius: 3px; z-index: 5; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.bullet-marker { position: absolute; top: -6px; width: 3px; height: 40px; z-index: 6; }
.bullet-marker.target-master { background: #1d4ed8; height: 82px; top: -54px; }
.bullet-marker.target-scvd { background: #7e22ce; height: 112px; top: -84px; }
.bullet-marker-label { position: absolute; transform: translateX(-50%); font-size: 10px; font-weight: 700; white-space: nowrap; text-align: center; padding: 3px 10px; border-radius: 3px; color: #fff; z-index: 7; box-shadow: 0 2px 4px rgba(0,0,0,0.15); }
.bullet-marker-label.actual { background: #0891B2; top: -34px; }
.bullet-marker-label.actual::after { content: ""; position: absolute; left: 50%; top: 22px; width: 3px; height: 40px; background: #0891B2; transform: translateX(-50%); z-index: 4; }
.bullet-marker-label.target-master { background: #1d4ed8; top: -58px; }
.bullet-marker-label.target-scvd { background: #7e22ce; top: -88px; }
.bullet-gap-box { border-left: 3px solid #ccc; padding: 10px 14px; margin-top: 6px; border-radius: 4px; }
.bullet-gap-title { font-weight: 700; margin-bottom: 6px; color: #1a1a1a; font-size: 11.5px; }
.bullet-gap-row { display: flex; gap: 10px; padding: 4px 0; color: #4b5563; font-size: 12px; align-items: center; flex-wrap: wrap; }
.bullet-gap-label { color: #6b7280; min-width: 260px; flex-shrink: 0; }
.bullet-gap-value { font-weight: 700; }
.bullet-gap-value.critical { color: #b04747; }
.bullet-gap-value.planning { color: #1d4ed8; }
.bullet-gap-value.ok { color: #2c6b3d; }
.bullet-gap-value.neutral { color: #4b5563; }
.mk-actual, .mk-target-master, .mk-target-scvd { color: #fff; font-weight: 700; padding: 2px 8px; border-radius: 3px; display: inline-block; line-height: 1.4; }
.mk-actual { background: #0891B2; }
.mk-target-master { background: #1d4ed8; }
.mk-target-scvd { background: #7e22ce; }
.gap-num { display: inline-block; background: #808080; color: #fff; width: 20px; height: 20px; line-height: 20px; text-align: center; border-radius: 3px; font-size: 12px; font-weight: 700; vertical-align: middle; }

/* ④ Heatmap */
.heatmap-warehouse { margin-bottom: 24px; }
.heatmap-warehouse:last-child { margin-bottom: 0; }
.heatmap-warehouse-title { font-size: 13.5px; font-weight: 700; color: #1a1a1a; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #FFDB00; display: flex; align-items: center; gap: 6px; justify-content: space-between; flex-wrap: wrap; }
.heatmap-warehouse-meta { font-size: 11px; color: #6b7280; font-weight: 500; }
.heatmap-scroll { overflow-x: auto; }
.heatmap-table { width: 100%; border-collapse: separate; border-spacing: 3px; font-size: 11px; min-width: 720px; }
.heatmap-table th { font-weight: 500; color: #6b7280; padding: 4px; text-align: center; font-size: 10.5px; }
.heatmap-table th.sku-header { text-align: left; width: 200px; padding-left: 6px; color: #1a1a1a; font-weight: 600; font-size: 11px; }
.heatmap-table th.current-month { background: #FFDB00; color: #1a1a1a; border-radius: 4px; font-weight: 700; }
.heatmap-sku-cell { padding-left: 6px; font-weight: 500; color: #1a1a1a; text-align: left; font-size: 11.5px; background: #f9fafb; border-radius: 4px; white-space: nowrap; }
.heatmap-cell { padding: 10px 2px; text-align: center; border-radius: 4px; font-size: 10px; font-weight: 700; cursor: pointer; transition: transform 0.1s; min-width: 34px; }
.heatmap-cell:hover { transform: scale(1.12); box-shadow: 0 3px 10px rgba(0,0,0,0.2); }
.heatmap-cell.current-col { outline: 2px solid #f59e0b; outline-offset: -2px; }
.heatmap-legend { display: flex; justify-content: center; gap: 16px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #f0f0f0; font-size: 10.5px; flex-wrap: wrap; }
.heatmap-legend-item { display: flex; align-items: center; gap: 5px; color: #4b5563; }
.heatmap-legend-swatch { width: 14px; height: 14px; border-radius: 3px; }

/* ⑤ / ⑥ Modals */
.scvd-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.55); z-index: 1050; display: flex; justify-content: center; align-items: center; }
.scvd-modal { background: #fff; border-radius: 12px; width: 860px; max-width: 92vw; max-height: 88vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.scvd-modal-header { padding: 16px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; background: linear-gradient(135deg, #FFDB00 0%, #f4c800 100%); }
.scvd-modal-header h2 { font-size: 17px; font-weight: 700; color: #1a1a1a; margin: 0; }
.scvd-modal-header .modal-subtitle { font-size: 11.5px; color: rgba(0,0,0,0.7); margin-top: 3px; }
.scvd-modal-close { background: rgba(0,0,0,0.08); border: none; font-size: 18px; cursor: pointer; color: #1a1a1a; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.scvd-modal-close:hover { background: rgba(0,0,0,0.18); }
.scvd-modal-body { overflow-y: auto; padding: 22px 24px; background: #fafbfc; }

/* formula modal — spec v3 (TOC + widget-groups + visual mockups) */
.formula-section { margin-bottom: 16px; padding: 14px 16px; background: #fff; border-radius: 8px; border: 1px solid #e5e7eb; border-left: 4px solid #FFDB00; }
.formula-section:last-child { margin-bottom: 0; }
.formula-title { font-size: 14px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.formula-number { background: #FFDB00; color: #1a1a1a; width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; flex-shrink: 0; }
.formula-code { font-family: "Consolas", "Courier New", monospace; background: #f8fafc; color: #0f172a; border: 1px solid #e2e8f0; border-left: 4px solid #58595B; padding: 14px 18px; border-radius: 6px; font-size: 13px; line-height: 2; margin: 8px 0; white-space: pre-wrap; overflow-x: auto; }
.formula-example { font-size: 12px; color: #4b5563; padding: 10px 14px; background: #f0fdf4; border-radius: 5px; margin-top: 8px; border-left: 3px solid #22c55e; line-height: 1.8; }
.formula-example b { color: #1a1a1a; }
.formula-source { font-size: 11px; color: #6b7280; font-style: italic; margin-top: 6px; padding-left: 4px; line-height: 1.6; }
.formula-important { font-size: 11.5px; color: #7f1d1d; background: #fef2f2; padding: 8px 12px; border-radius: 4px; margin-top: 6px; border-left: 3px solid #FF6B6B; line-height: 1.5; }
.formula-location { background: #eff6ff; border-left: 3px solid #3b82f6; padding: 8px 12px; border-radius: 4px; font-size: 12px; color: #1e40af; margin-bottom: 10px; font-weight: 500; line-height: 1.5; }
.formula-visual { background: #f8fafc; padding: 14px; border-radius: 6px; margin: 8px 0; border: 1px dashed #cbd5e1; }
.formula-visual-label { font-size: 10.5px; color: #6b7280; font-weight: 600; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
.visual-mockup { background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 12px 14px; font-size: 11px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); margin-bottom: 8px; }
.visual-mockup:last-child { margin-bottom: 0; }
.visual-highlight { background: #fef3c7; outline: 2px solid #fbbf24; border-radius: 3px; padding: 1px 5px; font-weight: 700; color: #78350f; display: inline-block; }
.visual-arrow { color: #dc2626; font-weight: 700; font-size: 11px; margin-left: 4px; }

/* formula modal — widget grouping */
.widget-group { margin-bottom: 26px; }
.widget-group:last-child { margin-bottom: 0; }
.widget-group-title { font-size: 15px; font-weight: 800; color: #fff; background: linear-gradient(135deg, #1a1a1a 0%, #4b5563 100%); padding: 12px 18px; border-radius: 8px 8px 0 0; display: flex; align-items: center; gap: 8px; scroll-margin-top: 10px; }
.widget-group-body { background: #fef9e7; border: 1px solid #fbbf24; border-top: none; padding: 14px; border-radius: 0 0 8px 8px; }
.widget-group-body .formula-section { background: #fff; border-left: 3px solid #FFDB00; }

/* formula modal — table of contents */
.modal-toc { background: linear-gradient(135deg, #fef9e7 0%, #fff8dc 100%); border: 1px solid #fbbf24; border-radius: 8px; padding: 14px 16px; margin-bottom: 20px; }
.modal-toc-title { font-size: 12px; font-weight: 700; color: #7c2d12; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.modal-toc-list { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.modal-toc-item { padding: 6px 10px; background: #fff; border: 1px solid #fde68a; border-radius: 5px; font-size: 12px; color: #1a1a1a; text-decoration: none; cursor: pointer; transition: all 0.15s; display: flex; align-items: center; gap: 6px; }
.modal-toc-item:hover { background: #fef3c7; border-color: #f59e0b; transform: translateX(2px); }

/* formula modal — gap analysis 3-form cards (widget ③.4) */
.gap-forms-container { display: flex; flex-direction: column; gap: 12px; margin: 10px 0; }
.gap-form-card { border-radius: 8px; padding: 14px 18px; border-left: 5px solid; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.gap-form-1 { background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%); border-left-color: #FF6B6B; }
.gap-form-2 { background: linear-gradient(135deg, #faf5ff 0%, #fdf4ff 100%); border-left-color: #a855f7; }
.gap-form-3 { background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%); border-left-color: #3b82f6; }
.gap-form-header { font-weight: 800; font-size: 13.5px; margin-bottom: 4px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.gap-form-1 .gap-form-header { color: #b04747; }
.gap-form-2 .gap-form-header { color: #7e22ce; }
.gap-form-3 .gap-form-header { color: #1d4ed8; }
.gap-form-badge { background: #fff; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; }
.gap-form-question { font-size: 11.5px; font-style: italic; color: #4b5563; margin-bottom: 10px; padding-left: 4px; line-height: 1.5; }
.gap-form-formula { background: #fff; padding: 12px 16px; border-radius: 6px; font-family: "Consolas", "Courier New", monospace; font-size: 12px; line-height: 2; margin-bottom: 8px; white-space: pre-wrap; border: 1px solid #e5e7eb; color: #1e293b; overflow-x: auto; }
.gap-form-example { font-size: 11.5px; color: #1e293b; padding: 8px 12px; background: rgba(255,255,255,0.7); border-radius: 5px; line-height: 1.7; }
.gap-form-example b { font-weight: 700; }

/* ③ bullet gap subnote */
.bullet-gap-subnote { font-weight: 500; color: #94a3b8; font-size: 10px; }

/* ② urgent cards — left column layout + status swatch (#58/#60) */
.alert-top-left { display: flex; align-items: center; gap: 6px; }
.status-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 5px; vertical-align: middle; flex-shrink: 0; }

/* ② urgent section info icon + tooltip (#59) — hover-only (pure CSS, matches mockup) */
.info-tooltip { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; background: #cbd5e1; color: #475569; border-radius: 50%; font-size: 12px; font-weight: bold; margin-left: 8px; cursor: help; user-select: none; vertical-align: middle; }
.info-tooltip:hover { background: #FFDB00; color: #58595B; }
.info-tooltip::after { content: attr(data-tip); position: absolute; top: calc(100% + 8px); left: 0; background: #1f2937; color: #fff; padding: 10px 14px; border-radius: 6px; font-size: 12px; font-weight: normal; font-style: normal; line-height: 1.5; white-space: normal; width: max-content; max-width: 340px; z-index: 100; box-shadow: 0 6px 18px rgba(0,0,0,0.22); opacity: 0; visibility: hidden; transition: opacity 0.15s, visibility 0.15s; pointer-events: none; }
.info-tooltip::before { content: ''; position: absolute; top: calc(100% + 3px); left: 12px; border: 5px solid transparent; border-bottom-color: #1f2937; z-index: 100; opacity: 0; visibility: hidden; transition: opacity 0.15s, visibility 0.15s; pointer-events: none; }
.info-tooltip:hover::after, .info-tooltip:hover::before { opacity: 1; visibility: visible; }

/* setup modal */
.setup-modal-body { overflow-y: auto; padding: 0; background: #fafbfc; }
.setup-info-bar { background: #e8f4fd; border-left: 4px solid #2b8dd6; padding: 12px 22px; font-size: 13px; color: #444; }
.setup-warning-bar { background: #fff4e6; border-left: 4px solid #e3552c; padding: 12px 22px; font-size: 13px; color: #444; }
.setup-section { padding: 18px 24px; border-bottom: 1px solid #eaeaea; }
.setup-section-title { font-size: 15px; font-weight: bold; color: #58595B; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
table.threshold { width: 100%; border-collapse: collapse; font-size: 13px; }
table.threshold th { background: #58595B; color: #fff; padding: 10px 8px; text-align: center; font-weight: normal; font-size: 13px; }
table.threshold td { padding: 8px; border-bottom: 1px solid #eaeaea; text-align: center; vertical-align: middle; }
table.threshold input[type=number], table.threshold input[type=text] { padding: 6px 8px; border: 1px solid #cfd4d9; border-radius: 4px; font-size: 13px; width: 100%; font-family: inherit; }
table.threshold textarea { padding: 6px 8px; border: 1px solid #cfd4d9; border-radius: 4px; font-size: 13px; width: 100%; font-family: inherit; resize: vertical; min-height: 40px; }
/* #67: round color swatch — matches mockup .color-swatch (30×30, border-radius:50%, 2px white border + 1px gray ring); kept as functional <input type=color> */
.color-picker { width: 30px; height: 30px; border: 2px solid #ffffff; border-radius: 50%; box-shadow: 0 0 0 1px #cfd4d9; padding: 0; cursor: pointer; background: #fff; vertical-align: middle; }
.color-picker::-webkit-color-swatch-wrapper { padding: 0; }
.color-picker::-webkit-color-swatch { border: none; border-radius: 50%; }
.color-picker::-moz-color-swatch { border: none; border-radius: 50%; }
.btn-tdelete { background: transparent; border: 1px solid #d0d0d0; color: #b04747; padding: 5px 10px; border-radius: 4px; cursor: pointer; font-size: 13px; }
.btn-tdelete:hover:not(:disabled) { background: #ffe0e0; }
.btn-tdelete:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-tadd { background: #fff; border: 2px dashed #58595B; color: #58595B; padding: 10px 20px; border-radius: 6px; cursor: pointer; margin-top: 12px; font-size: 14px; display: block; width: 100%; }
.btn-tadd:disabled { opacity: 0.4; cursor: not-allowed; }
.note-below { margin-top: 10px; font-size: 12px; color: #777; font-style: italic; }
.target-row { display: flex; align-items: center; gap: 15px; font-size: 15px; color: #444; }
.target-input { padding: 8px 12px; border: 2px solid #FFDB00; border-radius: 6px; font-size: 16px; font-weight: bold; width: 100px; text-align: center; font-family: inherit; }
.setup-footer { padding: 16px 24px; background: #f8f9fa; display: flex; justify-content: flex-end; gap: 12px; border-top: 1px solid #e5e7eb; }
.btn-sf { padding: 12px 24px; border: none; border-radius: 6px; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: bold; }
.btn-sf:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-sf-primary { background: #58595B; color: #fff; }
.btn-sf-primary:hover:not(:disabled) { background: #3d3e40; }
.btn-sf-warning { background: #FFDB00; color: #58595B; }
.btn-sf-warning:hover:not(:disabled) { background: #e6c500; }
.badge-lock { display: inline-block; background: rgba(0,0,0,0.15); border-radius: 4px; padding: 2px 6px; font-size: 11px; margin-left: 4px; }
</style>



// WEBPACK FOOTER //
// App/Components/StockCoverDays/StockCoverDaysDashboard.vue